import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  Flex,
  useToast,
} from "@chakra-ui/react";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import ClassificationsEditor from "./ClassificationsEditor";
import ReferencesEditor from "./ReferencesEditor";
import CurrencyAmountInput from "./CurrencyAmountInput";
import RateAmountInput from "./RateAmountInput";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineTrash,
} from "react-icons/hi";
import PartySelectById from "./PartySelectById";
import PartySelect from "./PartySelect";
import AmountsEditor from "./AmountsEditor";
import LocationsEditor from "./LocationsEditor";
import DateTimesEditor from "./DateTimesEditor";

const GRLCPlacementEditor = ({ contractInstance, productTemplate }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [placement, setPlacement] = useState({
    placingStageCode: "build",
    placingTransactionFunctionCode: "submission",
    contract: {
      _id: "",
      name: "",
      type: "",
      typeDescription: "",
      natureCode: "",
      treatyFacCode: "",
      accountingBasis: "",
    },
    sections: [
      {
        _id: "",
        name: "",
        description: "",
        contractReportingLevelCode: "",
        riskOrInterestDescription: "",
        type: "",
        natureCode: "",
        treatyFacCode: "",
        coverType: "",
        coverOperatingBasis: "",
        contractAmountsCcy: "",
        references: [],
        period: {
          id: "",
          statusCode: "",
          description: "",
          scopeCode: "",
          extensionProvisionsDescription: "",
          startDateTime: {
            value: "",
            timeZoneCode: "",
          },
          endDateTime: {
            value: "",
            timeZoneCode: "",
          },
          anniversaryDateTime: "",
          underwritingYear: "",
          lineBasis: {
            writtenBasis: "",
            signedBasis: "",
          },
          orderShare: {
            amount: {
              ccy: "",
              value: 0,
            },
            percentage: {
              value: 0,
              unitCode: "",
            },
          },
          brokerShare: {
            amount: {
              ccy: "",
              value: 0,
            },
            percentage: {
              value: 0,
              unitCode: "",
            },
          },
          claimsMadeProvisions: {
            continuityDateTime: "",
            pendingAndPriorLitigationDateTime: "",
            coverageRetroactiveDateTime: "",
          },
          classes: [
            {
              id: "",
              agencyCode: "",
              agencyCodeSetCode: "",
              code: "",
              description: "",
              allocationPercentage: {
                value: 0,
                unitCode: "",
              },
              ruleCode: "",
            },
          ],
          perils: [
            {
              id: "",
              agencyCode: "",
              agencyCodeSetCode: "",
              code: "",
              description: "",
              allocationPercentage: {
                value: 0,
                unitCode: "",
              },
              ruleCode: "",
            },
          ],
          locations: [
            {
              id: "",
              type: "",
              agencyCode: "",
              agencyCodeSetCode: "",
              code: "",
              countryCode: "",
              subentityCode: "",
              supraentityCode: "",
              description: "",
              allocationPercentage: {
                value: 0,
                unitCode: "",
              },
              ruleCode: "",
            },
          ],
          coverages: [
            {
              id: "",
              type: "",
              description: "",
              amount: {
                shareCode: "",
                ccyIndicator: "",
                ccy: "",
                value: 0,
              },
              percentage: {
                value: 0,
                unitCode: "",
              },
              basis: [
                {
                  code: "",
                  operation: "",
                  subject: "",
                  description: "",
                },
              ],
              numberOfLines: 0,
              numberOfUnits: 0,
            },
          ],
          deductibles: [
            {
              id: "",
              type: "",
              description: "",
              amount: {
                shareCode: "",
                ccyIndicator: "",
                ccy: "",
                value: 0,
              },
              percentage: {
                value: 0,
                unitCode: "",
              },
              basis: [
                {
                  code: "",
                  operation: "",
                  subject: "",
                  description: "",
                },
              ],
              numberOfLines: 0,
              numberOfUnits: 0,
            },
          ],
          premiums: [
            {
              id: "",
              type: "",
              description: "",
              percentage: {
                value: 0,
                unitCode: "",
              },
              amount: {
                shareCode: "",
                ccyIndicator: "",
                ccy: "",
                value: 0,
              },
              adjustableIndicator: "",
              rate: {
                value: 0,
                unitCode: "",
              },
              minimumRate: {
                value: 0,
                unitCode: "",
              },
              maximumRate: {
                value: 0,
                unitCode: "",
              },
              incomeAmountBasis: "",
              termsOfTradePeriodDays: 0,
              numberOfInstalments: 0,
              instalments: [
                {
                  id: "",
                  sequenceNbr: 0,
                  amount: {
                    shareCode: "",
                    ccyIndicator: "",
                    ccy: "",
                    value: 0,
                  },
                  percentage: {
                    value: 0,
                    unitCode: "",
                  },
                  rate: {
                    value: 0,
                    unitCode: "",
                  },
                  incomeAmountBasis: "",
                  minimumRate: {
                    value: 0,
                    unitCode: "",
                  },
                  maximumRate: {
                    value: 0,
                    unitCode: "",
                  },
                  dueDateTime: {
                    timeZoneCode: "",
                    qualifier: "",
                    estimatedIndicator: "",
                    value: "",
                  },
                  termsOfTradePeriodDuration: 0,
                  termsOfTradePeriodBasis: "",
                  payableByDateTime: {
                    timeZoneCode: "",
                    qualifier: "",
                    estimatedIndicator: "",
                    value: "",
                  },
                  description: "",
                },
              ],
              premiumAdjustment: {
                id: "",
                description: "",
                creditPeriodDurationDays: 0,
                premiumAmountCurrencyCode: "",
                premiumAmount: {
                  shareCode: "",
                  ccyIndicator: "",
                  ccy: "",
                  value: 0,
                },
                rate: {
                  value: 0,
                  unitCode: "",
                },
                minimumRate: {
                  value: 0,
                  unitCode: "",
                },
                maximumRate: {
                  value: 0,
                  unitCode: "",
                },
                basis: "",
                calculationType: "",
                calculationLoadingOperationCode: "",
                calculationLoadingFractionNumerator: 0,
                calculationLoadingFractionDenominator: 0,
                calculationFrequencyCode: "",
                calculationPeriodStartDateTime: {
                  value: "",
                  timeZoneCode: "",
                },
                calculationPeriodEndDateTime: {
                  value: "",
                  timeZoneCode: "",
                },
              },
              taxes: [
                {
                  id: "",
                  description: "",
                  applicationType: "",
                  classCode: "",
                  type: "",
                  locations: [
                    {
                      id: "",
                      type: "",
                      agencyCode: "",
                      agencyCodeSetCode: "",
                      code: "",
                      countryCode: "",
                      subentityCode: "",
                      supraentityCode: "",
                      description: "",
                      allocationPercentage: {
                        value: 0,
                        unitCode: "",
                      },
                      ruleCode: "",
                    },
                  ],
                  rateBasis: "",
                  payableByParty: {
                    type: "",
                    id: "",
                  },
                  administratorParty: {
                    type: "",
                    id: "",
                  },
                  baseAmount: {
                    shareCode: "",
                    ccyIndicator: "",
                    ccy: "",
                    value: 0,
                  },
                  rate: {
                    value: 0,
                    unitCode: "",
                  },
                  unitCount: 0,
                  amount: {
                    shareCode: "",
                    ccyIndicator: "",
                    ccy: "",
                    value: 0,
                  },
                },
              ],
              commissions: [
                {
                  id: "",
                  type: "",
                  description: "",
                  classCode: "",
                  basis: "",
                  interpolationBasis: "",
                  lossRatioBasis: "",
                  carryForwardCode: "",
                  carryForwardIntervalPeriodType: "",
                  carryForwardInterval: 0,
                  calculationType: "",
                  calculationFrequencyCode: "",
                  firstCalculationDateTime: {
                    value: "",
                    timeZoneCode: "",
                  },
                  calculationStartDateTime: {
                    value: "",
                    timeZoneCode: "",
                  },
                  calculationEndDateTime: {
                    value: "",
                    timeZoneCode: "",
                  },
                  managementExpensesPercentage: {
                    value: 0,
                    unitCode: "",
                  },
                  payableToParty: {
                    type: "",
                    id: "",
                  },
                  payableByParty: {
                    type: "",
                    id: "",
                  },
                  commissionValues: [
                    {
                      basis: "",
                      sequenceNbr: 0,
                      percentage: {
                        value: 0,
                        unitCode: "",
                      },
                      lossRatioPercentage: {
                        value: 0,
                        unitCode: "",
                      },
                      cedingCommissionPercentage: {
                        value: 0,
                        unitCode: "",
                      },
                      amount: {
                        shareCode: "",
                        ccyIndicator: "",
                        ccy: "",
                        value: 0,
                      },
                    },
                  ],
                },
              ],
              otherDeductions: [
                {
                  id: "",
                  type: "",
                  description: "",
                  classCode: "",
                  percentage: {
                    value: 0,
                    unitCode: "",
                  },
                  basis: "",
                  amount: {
                    shareCode: "",
                    ccyIndicator: "",
                    ccy: "",
                    value: 0,
                  },
                  payableToParty: {
                    type: "",
                    id: "",
                  },
                  payableByParty: {
                    type: "",
                    id: "",
                  },
                  serviceProvidedToParty: {
                    type: "",
                    id: "",
                  },
                },
              ],
              brokerages: [
                {
                  id: "",
                  type: "",
                  description: "",
                  classCode: "",
                  percentage: {
                    value: 0,
                    unitCode: "",
                  },
                  basis: "",
                  amount: {
                    shareCode: "",
                    ccyIndicator: "",
                    ccy: "",
                    value: 0,
                  },
                  baseAmount: {
                    shareCode: "",
                    ccyIndicator: "",
                    ccy: "",
                    value: 0,
                  },
                  payableToParty: {
                    type: "",
                    id: "",
                  },
                  payableByParty: {
                    type: "",
                    id: "",
                  },
                },
              ],
              reinstatements: [
                {
                  id: "",
                  sequenceNbr: 0,
                  description: "",
                  unlimitedReinstatementsIndicator: "",
                  numberOfReinstatements: 0,
                  brokeragePercentage: {
                    value: 0,
                    unitCode: "",
                  },
                  brokerageAmount: {
                    shareCode: "",
                    ccyIndicator: "",
                    ccy: "",
                    value: 0,
                  },
                  calculationType: "",
                  premiumRate: {
                    value: 0,
                    unitCode: "",
                  },
                  premiumAmount: {
                    shareCode: "",
                    ccyIndicator: "",
                    ccy: "",
                    value: 0,
                  },
                },
              ],
              contractParties: [
                {
                  id: "",
                  type: "",
                  roleCode: "",
                  participantFunctionCode: "",
                  party: {
                    _id: "",
                    name: "",
                    type: "",
                    roleCode: "",
                    partyIdentities: [
                      {
                        id: "",
                        agencyCode: "",
                      },
                    ],
                    address: {
                      type: "",
                      numberAndStreet: "",
                      subentityCode: "",
                      cityName: "",
                      postalCode: "",
                      countryCode: "",
                    },
                    contact: {
                      description: "",
                      roleCode: "",
                      personName: "",
                      telephone: "",
                      fax: "",
                      email: "",
                    },
                  },
                  references: [
                    {
                      ownerCode: "",
                      type: "",
                      value: "",
                    },
                  ],
                  classifications: [
                    {
                      type: "",
                      agencyCode: "",
                      agencyCodeSetCode: "",
                      description: "",
                      code: "",
                      ruleCode: "",
                    },
                  ],
                },
              ],
              contractMarkets: [
                {
                  id: "",
                  party: {
                    _id: "",
                    name: "",
                    type: "",
                    roleCode: "",
                    partyIdentities: [
                      {
                        id: "",
                        agencyCode: "",
                      },
                    ],
                    address: {
                      type: "",
                      numberAndStreet: "",
                      subentityCode: "",
                      cityName: "",
                      postalCode: "",
                      countryCode: "",
                    },
                    contact: {
                      description: "",
                      roleCode: "",
                      personName: "",
                      telephone: "",
                      fax: "",
                      email: "",
                    },
                  },
                  participationBasis: "",
                  references: [
                    {
                      ownerCode: "",
                      type: "",
                      value: "",
                    },
                  ],
                  writtenDateTime: "",
                  participantFunctionCode: "",
                  endorsementParticipantFunctionCode: "",
                  claimParticipantFunctionCode: "",
                  declarationParticipantFunctionCode: "",
                  lineToStandIndicator: "",
                  minimumShare: 0,
                  maximumShare: 0,
                  quotedShare: 0,
                  writtenShare: 0,
                  signedShare: 0,
                  classifications: [
                    {
                      type: "",
                      agencyCode: "",
                      agencyCodeSetCode: "",
                      description: "",
                      code: "",
                      ruleCode: "",
                    },
                  ],
                },
              ],
              schedules: [
                {
                  id: "",
                  type: "",
                  description: "",
                  scheduleEntries: [
                    {
                      id: "",
                      type: "",
                      classCode: "",
                      parentId: "",
                      parentType: "",
                      parentClassCode: "",
                      metadataStandardCode: "",
                      name: "",
                      description: "",
                      quantity: 0,
                      parties: [
                        {
                          _id: "",
                          type: "",
                          roleCode: "",
                          partyIdentities: [
                            {
                              id: "",
                              agencyCode: "",
                            },
                          ],
                          name: "",
                          address: {
                            type: "",
                            numberAndStreet: "",
                            subentityCode: "",
                            cityName: "",
                            postalCode: "",
                            countryCode: "",
                          },
                          contact: {
                            description: "",
                            roleCode: "",
                            personName: "",
                            telephone: "",
                            fax: "",
                            email: "",
                          },
                          createdDate: "",
                        },
                      ],
                      addresses: [
                        {
                          type: "",
                          numberAndStreet: "",
                          subentityCode: "",
                          cityName: "",
                          postalCode: "",
                          countryCode: "",
                        },
                      ],
                      references: [
                        {
                          ownerCode: "",
                          type: "",
                          value: "",
                        },
                      ],
                      classifications: [
                        {
                          type: "",
                          agencyCode: "",
                          agencyCodeSetCode: "",
                          description: "",
                          code: "",
                          ruleCode: "",
                        },
                      ],
                      amounts: [
                        {
                          type: "",
                          unitCode: "",
                          ccy: "",
                          value: 0,
                          description: "",
                        },
                      ],
                      dateTimes: [
                        {
                          type: "",
                          format: "",
                          timeZoneCode: "",
                          value: "",
                          description: "",
                        },
                      ],
                      text: [
                        {
                          type: "",
                          agencyCode: "",
                          agencyCodeSetCode: "",
                          reference: "",
                          name: "",
                          value: "",
                        },
                      ],
                      coverageAttachments: [
                        {
                          value: "",
                        },
                      ],
                      clauseAttachments: [
                        {
                          value: "",
                        },
                      ],
                    },
                  ],
                  entryRules: [
                    {
                      genericType: "",
                      type: "",
                      requirement: "",
                      unlimited: "",
                      allowedCount: 0,
                    },
                  ],
                },
              ],
              regulatoryProvisions: [
                {
                  id: "",
                  regulatoryRiskLocations: [
                    {
                      id: "",
                      type: "",
                      agencyCode: "",
                      agencyCodeSetCode: "",
                      code: "",
                      countryCode: "",
                      subentityCode: "",
                      supraentityCode: "",
                      description: "",
                      allocationPercentage: {
                        value: 0,
                        unitCode: "",
                      },
                      ruleCode: "",
                    },
                  ],
                  classifications: [
                    {
                      type: "",
                      agencyCode: "",
                      agencyCodeSetCode: "",
                      description: "",
                      code: "",
                      ruleCode: "",
                    },
                  ],
                  amounts: [
                    {
                      type: "",
                      unitCode: "",
                      ccy: "",
                      value: 0,
                      description: "",
                    },
                  ],
                  dateTimes: [
                    {
                      type: "",
                      format: "",
                      timeZoneCode: "",
                      value: "",
                      description: "",
                    },
                  ],
                  allocations: [
                    {
                      type: "",
                      description: "",
                      code: "",
                      value: 0,
                      reference: "",
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
    ],
  });

  useEffect(() => {
    const base = contractInstance?.grlcPlacement || productTemplate || {};
    setPlacement({
      placingStageCode: base.placingStageCode || "build",
      placingTransactionFunctionCode:
        base.placingTransactionFunctionCode || "submission",
      contract: {
        _id: contractInstance?._id || "",
        name: base.contract?.name || "",
        type: base.contract?.type || "",
        typeDescription: base.contract?.typeDescription || "",
        natureCode: base.contract?.natureCode || "",
        treatyFacCode: base.contract?.treatyFacCode || "",
        accountingBasis: base.contract?.accountingBasis || "",
        references: base.contract?.references || [],
      },
      sections: (base.sections || []).map((section) => ({
        _id: section._id || crypto.randomUUID(),
        name: section.name || "",
        description: section.description || "",
        contractReportingLevelCode: section.contractReportingLevelCode || "",
        riskOrInterestDescription: section.riskOrInterestDescription || "",
        type: section.type || "",
        natureCode: section.natureCode || "",
        treatyFacCode: section.treatyFacCode || "",
        coverType: section.coverType || "",
        coverOperatingBasis: section.coverOperatingBasis || "",
        contractAmountsCcy: section.contractAmountsCcy || "",
        references: section.references || [],
        period: {
          id: section.period?.id || crypto.randomUUID(),
          statusCode: section.period?.statusCode || "",
          description: section.period?.description || "",
          scopeCode: section.period?.scopeCode || "",
          extensionProvisionsDescription:
            section.period?.extensionProvisionsDescription || "",
          startDateTime: {
            value: section.period?.startDateTime?.value || "",
            timeZoneCode: section.period?.startDateTime?.timeZoneCode || "",
          },
          endDateTime: {
            value: section.period?.endDateTime?.value || "",
            timeZoneCode: section.period?.endDateTime?.timeZoneCode || "",
          },
          anniversaryDateTime: section.period?.anniversaryDateTime || "",
          underwritingYear: section.period?.underwritingYear || "",
        },
        lineBasis: {
          writtenBasis: section.lineBasis?.writtenBasis || "",
          signedBasis: section.lineBasis?.signedBasis || "",
        },
        orderShare: {
          amount: {
            ccy: section.orderShare?.amount?.ccy || "",
            value: section.orderShare?.amount?.value || 0,
          },
          percentage: {
            value: section.orderShare?.percentage?.value || 0,
            unitCode: section.orderShare?.percentage?.unitCode || "",
          },
        },
        brokerShare: {
          amount: {
            ccy: section.brokerShare?.amount?.ccy || "",
            value: section.brokerShare?.amount?.value || 0,
          },
          percentage: {
            value: section.brokerShare?.percentage?.value || 0,
            unitCode: section.brokerShare?.percentage?.unitCode || "",
          },
        },
        claimsMadeProvisions: {
          continuityDateTime:
            section.claimsMadeProvisions?.continuityDateTime || "",
          pendingAndPriorLitigationDateTime:
            section.claimsMadeProvisions?.pendingAndPriorLitigationDateTime ||
            "",
          coverageRetroactiveDateTime:
            section.claimsMadeProvisions?.coverageRetroactiveDateTime || "",
        },
        classes: (section.classes || []).map((cls) => ({
          id: cls.id || crypto.randomUUID(),
          agencyCode: cls.agencyCode || "",
          agencyCodeSetCode: cls.agencyCodeSetCode || "",
          code: cls.code || "",
          description: cls.description || "",
          allocationPercentage: {
            value: cls.allocationPercentage?.value || 0,
            unitCode: cls.allocationPercentage?.unitCode || "",
          },
          ruleCode: cls.ruleCode || "",
        })),
        perils: (section.perils || []).map((peril) => ({
          id: peril.id || crypto.randomUUID(),
          agencyCode: peril.agencyCode || "",
          agencyCodeSetCode: peril.agencyCodeSetCode || "",
          code: peril.code || "",
          description: peril.description || "",
          allocationPercentage: {
            value: peril.allocationPercentage?.value || 0,
            unitCode: peril.allocationPercentage?.unitCode || "",
          },
          ruleCode: peril.ruleCode || "",
        })),
        locations: (section.locations || []).map((location) => ({
          id: location.id || crypto.randomUUID(),
          type: location.type || "",
          agencyCode: location.agencyCode || "",
          agencyCodeSetCode: location.agencyCodeSetCode || "",
          code: location.code || "",
          countryCode: location.countryCode || "",
          subentityCode: location.subentityCode || "",
          supraentityCode: location.supraentityCode || "",
          description: location.description || "",
          allocationPercentage: {
            value: location.allocationPercentage?.value || 0,
            unitCode: location.allocationPercentage?.unitCode || "",
          },
          ruleCode: location.ruleCode || "",
        })),
        coverages: (section.coverages || []).map((coverage) => ({
          id: coverage.id || crypto.randomUUID(),
          type: coverage.type || "",
          description: coverage.description || "",
          amount: {
            shareCode: coverage.amount?.shareCode || "",
            ccyIndicator: coverage.amount?.ccyIndicator || "",
            ccy: coverage.amount?.ccy || "",
            value: coverage.amount?.value || 0,
          },
          percentage: {
            value: coverage.percentage?.value || 0,
            unitCode: coverage.percentage?.unitCode || "",
          },
          basis: coverage.basis || [
            {
              code: "",
              operation: "",
              subject: "",
              description: "",
            },
          ],
          numberOfLines: coverage.numberOfLines || 0,
          numberOfUnits: coverage.numberOfUnits || 0,
        })),
        deductibles: (section.deductibles || []).map((deductible) => ({
          id: deductible.id || crypto.randomUUID(),
          type: deductible.type || "",
          description: deductible.description || "",
          amount: {
            shareCode: deductible.amount?.shareCode || "",
            ccyIndicator: deductible.amount?.ccyIndicator || "",
            ccy: deductible.amount?.ccy || "",
            value: deductible.amount?.value || 0,
          },
          percentage: {
            value: deductible.percentage?.value || 0,
            unitCode: deductible.percentage?.unitCode || "",
          },
          basis: deductible.basis || [
            {
              code: "",
              operation: "",
              subject: "",
              description: "",
            },
          ],
          numberOfLines: deductible.numberOfLines || 0,
          numberOfUnits: deductible.numberOfUnits || 0,
        })),
        premiums: (section.premiums || []).map((premium) => ({
          id: premium.id || crypto.randomUUID(),
          type: premium.type || "",
          description: premium.description || "",
          percentage: {
            value: premium.percentage?.value || 0,
            unitCode: premium.percentage?.unitCode || "",
          },
          amount: {
            shareCode: premium.amount?.shareCode || "",
            ccyIndicator: premium.amount?.ccyIndicator || "",
            ccy: premium.amount?.ccy || "",
            value: premium.amount?.value || 0,
          },
          adjustableIndicator: premium.adjustableIndicator || "",
          rate: {
            value: premium.rate?.value || 0,
            unitCode: premium.rate?.unitCode || "",
          },
          minimumRate: {
            value: premium.minimumRate?.value || 0,
            unitCode: premium.minimumRate?.unitCode || "",
          },
          maximumRate: {
            value: premium.maximumRate?.value || 0,
            unitCode: premium.maximumRate?.unitCode || "",
          },
          incomeAmountBasis: premium.incomeAmountBasis || "",
          termsOfTradePeriodDays: premium.termsOfTradePeriodDays || 0,
          numberOfInstalments: premium.numberOfInstalments || 0,
          instalments: (premium.instalments || []).map((instalment) => ({
            id: instalment.id || crypto.randomUUID(),
            sequenceNbr: instalment.sequenceNbr || 0,
            amount: {
              shareCode: instalment.amount?.shareCode || "",
              ccyIndicator: instalment.amount?.ccyIndicator || "",
              ccy: instalment.amount?.ccy || "",
              value: instalment.amount?.value || 0,
            },
            percentage: {
              value: instalment.percentage?.value || 0,
              unitCode: instalment.percentage?.unitCode || "",
            },
            rate: {
              value: instalment.rate?.value || 0,
              unitCode: instalment.rate?.unitCode || "",
            },
            minimumRate: {
              value: instalment.minimumRate?.value || 0,
              unitCode: instalment.minimumRate?.unitCode || "",
            },
            maximumRate: {
              value: instalment.maximumRate?.value || 0,
              unitCode: instalment.maximumRate?.unitCode || "",
            },
            incomeAmountBasis: instalment.incomeAmountBasis || "",
            dueDateTime: {
              timeZoneCode: instalment.dueDateTime?.timeZoneCode || "",
              qualifier: instalment.dueDateTime?.qualifier || "",
              estimatedIndicator:
                instalment.dueDateTime?.estimatedIndicator || "",
              value: instalment.dueDateTime?.value || "",
            },
            termsOfTradePeriodDuration:
              instalment.termsOfTradePeriodDuration || 0,
            termsOfTradePeriodBasis: instalment.termsOfTradePeriodBasis || "",
            payableByDateTime: {
              timeZoneCode: instalment.dueDateTime?.timeZoneCode || "",
              qualifier: instalment.dueDateTime?.qualifier || "",
              estimatedIndicator:
                instalment.dueDateTime?.estimatedIndicator || "",
              value: instalment.dueDateTime?.value || "",
            },
            description: instalment.description || "",
          })),
        })),
        premiumAdjustment: section.premiumAdjustment || {
          id: crypto.randomUUID(),
          description: "",
          creditPeriodDurationDays: 0,
          premiumAmountCurrencyCode: "",
          premiumAmount: {
            shareCode: "",
            ccyIndicator: "",
            ccy: "",
            value: 0,
          },
          rate: {
            value: 0,
            unitCode: "",
          },
          minimumRate: {
            value: 0,
            unitCode: "",
          },
          maximumRate: {
            value: 0,
            unitCode: "",
          },
          basis: "",
          calculationType: "",
          calculationLoadingOperationCode: "",
          calculationLoadingFractionNumerator: 0,
          calculationLoadingFractionDenominator: 0,
          calculationFrequencyCode: "",
          calculationPeriodStartDateTime: {
            value: "",
            timeZoneCode: "",
          },
          calculationPeriodEndDateTime: {
            value: "",
            timeZoneCode: "",
          },
        },
        taxes: (section.taxes || []).map((tax) => ({
          id: tax.id || crypto.randomUUID(),
          description: tax.description || "",
          applicationType: tax.applicationType || "",
          classCode: tax.classCode || "",
          type: tax.type || "",
          locations: (tax.locations || []).map((location) => ({
            id: location.id || crypto.randomUUID(),
            type: location.type || "",
            agencyCode: location.agencyCode || "",
            agencyCodeSetCode: location.agencyCodeSetCode || "",
            code: location.code || "",
            countryCode: location.countryCode || "",
            subentityCode: location.subentityCode || "",
            supraentityCode: location.supraentityCode || "",
            description: location.description || "",
            allocationPercentage: {
              value: location.allocationPercentage?.value || 0,
              unitCode: location.allocationPercentage?.unitCode || "",
            },
            ruleCode: location.ruleCode || "",
          })),
          rateBasis: tax.rateBasis || "",
          payableByParty: {
            type: tax.payableByParty?.type || "",
            id: tax.payableByParty?.id || "",
          },
          administratorParty: {
            type: tax.administratorParty?.type || "",
            id: tax.administratorParty?.id || "",
          },
          baseAmount: {
            shareCode: tax.baseAmount?.shareCode || "",
            ccyIndicator: tax.baseAmount?.ccyIndicator || "",
            ccy: tax.baseAmount?.ccy || "",
            value: tax.baseAmount?.value || 0,
          },
          rate: {
            value: tax.rate?.value || 0,
            unitCode: tax.rate?.unitCode || "",
          },
          unitCount: tax.unitCount || 0,
          amount: {
            shareCode: tax.amount?.shareCode || "",
            ccyIndicator: tax.amount?.ccyIndicator || "",
            ccy: tax.amount?.ccy || "",
            value: tax.amount?.value || 0,
          },
        })),
        commissions: (section.commissions || []).map((commission) => ({
          id: commission.id || crypto.randomUUID(),
          type: commission.type || "",
          description: commission.description || "",
          classCode: commission.classCode || "",
          basis: commission.basis || "",
          interpolationBasis: commission.interpolationBasis || "",
          lossRatioBasis: commission.lossRatioBasis || "",
          carryForwardCode: commission.carryForwardCode || "",
          carryForwardIntervalPeriodType:
            commission.carryForwardIntervalPeriodType || "",
          carryForwardInterval: commission.carryForwardInterval || 0,
          calculationType: commission.calculationType || "",
          calculationFrequencyCode: commission.calculationFrequencyCode || "",
          firstCalculationDateTime: {
            value: commission.firstCalculationDateTime?.value || "",
            timeZoneCode:
              commission.firstCalculationDateTime?.timeZoneCode || "",
          },
          calculationStartDateTime: {
            value: commission.calculationStartDateTime?.value || "",
            timeZoneCode:
              commission.calculationStartDateTime?.timeZoneCode || "",
          },
          calculationEndDateTime: {
            value: commission.calculationEndDateTime?.value || "",
            timeZoneCode: commission.calculationEndDateTime?.timeZoneCode || "",
          },
          managementExpensesPercentage: {
            value: commission.managementExpensesPercentage?.value || 0,
            unitCode: commission.managementExpensesPercentage?.unitCode || "",
          },
          payableToParty: {
            type: commission.payableToParty?.type || "",
            id: commission.payableToParty?.id || "",
          },
          payableByParty: {
            type: commission.payableByParty?.type || "",
            id: commission.payableByParty?.id || "",
          },
          commissionValues: (commission.commissionValues || []).map(
            (value) => ({
              basis: value.basis || "",
              sequenceNbr: value.sequenceNbr || 0,
              percentage: {
                value: value.percentage?.value || 0,
                unitCode: value.percentage?.unitCode || "",
              },
              lossRatioPercentage: {
                value: value.lossRatioPercentage?.value || 0,
                unitCode: value.lossRatioPercentage?.unitCode || "",
              },
              cedingCommissionPercentage: {
                value: value.cedingCommissionPercentage?.value || 0,
                unitCode: value.cedingCommissionPercentage?.unitCode || "",
              },
              amount: {
                shareCode: value.amount?.shareCode || "",
                ccyIndicator: value.amount?.ccyIndicator || "",
                ccy: value.amount?.ccy || "",
                value: value.amount?.value || 0,
              },
            })
          ),
        })),
        otherDeductions: (section.otherDeductions || []).map((deduction) => ({
          id: deduction.id || crypto.randomUUID(),
          type: deduction.type || "",
          description: deduction.description || "",
          classCode: deduction.classCode || "",
          percentage: {
            value: deduction.percentage?.value || 0,
            unitCode: deduction.percentage?.unitCode || "",
          },
          basis: deduction.basis || "",
          amount: {
            shareCode: deduction.amount?.shareCode || "",
            ccyIndicator: deduction.amount?.ccyIndicator || "",
            ccy: deduction.amount?.ccy || "",
            value: deduction.amount?.value || 0,
          },
          payableToParty: {
            type: deduction.payableToParty?.type || "",
            id: deduction.payableToParty?.id || "",
          },
          payableByParty: {
            type: deduction.payableByParty?.type || "",
            id: deduction.payableByParty?.id || "",
          },
          serviceProvidedToParty: {
            type: deduction.serviceProvidedToParty?.type || "",
            id: deduction.serviceProvidedToParty?.id || "",
          },
        })),
        brokerages: (section.brokerages || []).map((brokerage) => ({
          id: brokerage.id || crypto.randomUUID(),
          type: brokerage.type || "",
          description: brokerage.description || "",
          classCode: brokerage.classCode || "",
          percentage: {
            value: brokerage.percentage?.value || 0,
            unitCode: brokerage.percentage?.unitCode || "",
          },
          basis: brokerage.basis || "",
          amount: {
            shareCode: brokerage.amount?.shareCode || "",
            ccyIndicator: brokerage.amount?.ccyIndicator || "",
            ccy: brokerage.amount?.ccy || "",
            value: brokerage.amount?.value || 0,
          },
          baseAmount: {
            shareCode: brokerage.baseAmount?.shareCode || "",
            ccyIndicator: brokerage.baseAmount?.ccyIndicator || "",
            ccy: brokerage.baseAmount?.ccy || "",
            value: brokerage.baseAmount?.value || 0,
          },
          payableToParty: {
            type: brokerage.payableToParty?.type || "",
            id: brokerage.payableToParty?.id || "",
          },
          payableByParty: {
            type: brokerage.payableByParty?.type || "",
            id: brokerage.payableByParty?.id || "",
          },
        })),
        reinstatements: (section.reinstatements || []).map((reinstatement) => ({
          id: reinstatement.id || crypto.randomUUID(),
          sequenceNbr: reinstatement.sequenceNbr || 0,
          description: reinstatement.description || "",
          unlimitedReinstatementsIndicator:
            reinstatement.unlimitedReinstatementsIndicator || "",
          numberOfReinstatements: reinstatement.numberOfReinstatements || 0,
          brokeragePercentage: {
            value: reinstatement.brokeragePercentage?.value || 0,
            unitCode: reinstatement.brokeragePercentage?.unitCode || "",
          },
          brokerageAmount: {
            shareCode: reinstatement.brokerageAmount?.shareCode || "",
            ccyIndicator: reinstatement.brokerageAmount?.ccyIndicator || "",
            ccy: reinstatement.brokerageAmount?.ccy || "",
            value: reinstatement.brokerageAmount?.value || 0,
          },
          calculationType: reinstatement.calculationType || "",
          premiumRate: {
            value: reinstatement.premiumRate?.value || 0,
            unitCode: reinstatement.premiumRate?.unitCode || "",
          },
          premiumAmount: {
            shareCode: reinstatement.premiumAmount?.shareCode || "",
            ccyIndicator: reinstatement.premiumAmount?.ccyIndicator || "",
            ccy: reinstatement.premiumAmount?.ccy || "",
            value: reinstatement.premiumAmount?.value || 0,
          },
        })),
        contractParties: (section.contractParties || []).map(
          (contractParty) => ({
            id: contractParty.id || crypto.randomUUID(),
            type: contractParty.type || "",
            roleCode: contractParty.roleCode || "",
            participantFunctionCode:
              contractParty.participantFunctionCode || "",
            party: {
              _id: contractParty.party?._id || crypto.randomUUID(),
              name: contractParty.party?.name || "",
              type: contractParty.party?.type || "",
              roleCode: contractParty.party?.roleCode || "",
              partyIdentities: (contractParty.party?.partyIdentities || []).map(
                (identity) => ({
                  id: identity.id || "",
                  agencyCode: identity.agencyCode || "",
                })
              ),
              address: {
                type: contractParty.party?.address?.type || "",
                numberAndStreet:
                  contractParty.party?.address?.numberAndStreet || "",
                subentityCode:
                  contractParty.party?.address?.subentityCode || "",
                cityName: contractParty.party?.address?.cityName || "",
                postalCode: contractParty.party?.address?.postalCode || "",
                countryCode: contractParty.party?.address?.countryCode || "",
              },
              contact: {
                description: contractParty.party?.contact?.description || "",
                roleCode: contractParty.party?.contact?.roleCode || "",
                personName: contractParty.party?.contact?.personName || "",
                telephone: contractParty.party?.contact?.telephone || "",
                fax: contractParty.party?.contact?.fax || "",
                email: contractParty.party?.contact?.email || "",
              },
            },
            references: (contractParty.references || []).map((ref) => ({
              ownerCode: ref.ownerCode || "",
              type: ref.type || "",
              value: ref.value || "",
            })),
            classifications: (contractParty.classifications || []).map(
              (cls) => ({
                type: cls.type || "",
                agencyCode: cls.agencyCode || "",
                agencyCodeSetCode: cls.agencyCodeSetCode || "",
                description: cls.description || "",
                code: cls.code || "",
                ruleCode: cls.ruleCode || "",
              })
            ),
          })
        ),
        contractMarkets: (section.contractMarkets || []).map((market) => ({
          id: market.id || crypto.randomUUID(),
          party: {
            _id: market.party?._id || crypto.randomUUID(),
            name: market.party?.name || "",
            type: market.party?.type || "",
            roleCode: market.party?.roleCode || "",
            partyIdentities: (market.party?.partyIdentities || []).map(
              (identity) => ({
                id: identity.id || "",
                agencyCode: identity.agencyCode || "",
              })
            ),
            address: {
              type: market.party?.address?.type || "",
              numberAndStreet: market.party?.address?.numberAndStreet || "",
              subentityCode: market.party?.address?.subentityCode || "",
              cityName: market.party?.address?.cityName || "",
              postalCode: market.party?.address?.postalCode || "",
              countryCode: market.party?.address?.countryCode || "",
            },
            contact: {
              description: market.party?.contact?.description || "",
              roleCode: market.party?.contact?.roleCode || "",
              personName: market.party?.contact?.personName || "",
              telephone: market.party?.contact?.telephone || "",
              fax: market.party?.contact?.fax || "",
              email: market.party?.contact?.email || "",
            },
          },
          participationBasis: market.participationBasis || "",
          references: (market.references || []).map((ref) => ({
            ownerCode: ref.ownerCode || "",
            type: ref.type || "",
            value: ref.value || "",
          })),
          writtenDateTime: market.writtenDateTime || "",
          participantFunctionCode: market.participantFunctionCode || "",
          endorsementParticipantFunctionCode:
            market.endorsementParticipantFunctionCode || "",
          claimParticipantFunctionCode:
            market.claimParticipantFunctionCode || "",
          declarationParticipantFunctionCode:
            market.declarationParticipantFunctionCode || "",
          lineToStandIndicator: market.lineToStandIndicator || "",
          minimumShare: market.minimumShare || 0,
          maximumShare: market.maximumShare || 0,
          quotedShare: market.quotedShare || 0,
          writtenShare: market.writtenShare || 0,
          signedShare: market.signedShare || 0,
          classifications: (market.classifications || []).map((cls) => ({
            type: cls.type || "",
            agencyCode: cls.agencyCode || "",
            agencyCodeSetCode: cls.agencyCodeSetCode || "",
            description: cls.description || "",
            code: cls.code || "",
            ruleCode: cls.ruleCode || "",
          })),
        })),
        schedules: (section.schedules || []).map((schedule) => ({
          id: schedule.id || crypto.randomUUID(),
          type: schedule.type || "",
          description: schedule.description || "",
          scheduleEntries: (schedule.scheduleEntries || []).map((entry) => ({
            id: entry.id || crypto.randomUUID(),
            type: entry.type || "",
            classCode: entry.classCode || "",
            parentId: entry.parentId || "",
            parentType: entry.parentType || "",
            parentClassCode: entry.parentClassCode || "",
            metadataStandardCode: entry.metadataStandardCode || "",
            name: entry.name || "",
            description: entry.description || "",
            quantity: entry.quantity || 0,
            parties: (entry.parties || []).map((party) => ({
              _id: party._id || crypto.randomUUID(),
              type: party.type || "",
              roleCode: party.roleCode || "",
              partyIdentities: (party.partyIdentities || []).map(
                (identity) => ({
                  id: identity.id || "",
                  agencyCode: identity.agencyCode || "",
                })
              ),
              name: party.name || "",
              address: {
                type: party.address?.type || "",
                numberAndStreet: party.address?.numberAndStreet || "",
                subentityCode: party.address?.subentityCode || "",
                cityName: party.address?.cityName || "",
                postalCode: party.address?.postalCode || "",
                countryCode: party.address?.countryCode || "",
              },
              contact: {
                description: party.contact?.description || "",
                roleCode: party.contact?.roleCode || "",
                personName: party.contact?.personName || "",
                telephone: party.contact?.telephone || "",
                fax: party.contact?.fax || "",
                email: party.contact?.email || "",
              },
            })),
            addresses: (entry.addresses || []).map((address) => ({
              type: address.type || "",
              numberAndStreet: address.numberAndStreet || "",
              subentityCode: address.subentityCode || "",
              cityName: address.cityName || "",
              postalCode: address.postalCode || "",
              countryCode: address.countryCode || "",
            })),
            references: (entry.references || []).map((ref) => ({
              ownerCode: ref.ownerCode || "",
              type: ref.type || "",
              value: ref.value || "",
            })),
            classifications: (entry.classifications || []).map((cls) => ({
              type: cls.type || "",
              agencyCode: cls.agencyCode || "",
              agencyCodeSetCode: cls.agencyCodeSetCode || "",
              description: cls.description || "",
              code: cls.code || "",
              ruleCode: cls.ruleCode || "",
            })),
            amounts: (entry.amounts || []).map((amount) => ({
              type: amount.type || "",
              unitCode: amount.unitCode || "",
              ccy: amount.ccy || "",
              value: amount.value || 0,
              description: amount.description || "",
            })),
            dateTimes: (entry.dateTimes || []).map((dateTime) => ({
              type: dateTime.type || "",
              format: dateTime.format || "",
              timeZoneCode: dateTime.timeZoneCode || "",
              value: dateTime.value || "",
              description: dateTime.description || "",
            })),
            text: (entry.text || []).map((text) => ({
              type: text.type || "",
              agencyCode: text.agencyCode || "",
              agencyCodeSetCode: text.agencyCodeSetCode || "",
              reference: text.reference || "",
              name: text.name || "",
              value: text.value || "",
            })),
            coverageAttachments: (entry.coverageAttachments || []).map(
              (attachment) => ({
                value: attachment.value || "",
              })
            ),
            clauseAttachments: (entry.clauseAttachments || []).map(
              (attachment) => ({
                value: attachment.value || "",
              })
            ),
          })),
          entryRules: (schedule.entryRules || []).map((rule) => ({
            genericType: rule.genericType || "",
            type: rule.type || "",
            requirement: rule.requirement || "",
            unlimited: rule.unlimited || "",
            allowedCount: rule.allowedCount || 0,
          })),
        })),
        regulatoryProvisions: (section.regulatoryProvisions || []).map(
          (provision) => ({
            id: provision.id || crypto.randomUUID(),
            regulatoryRiskLocations: (
              provision.regulatoryRiskLocations || []
            ).map((location) => ({
              id: location.id || crypto.randomUUID(),
              type: location.type || "",
              agencyCode: location.agencyCode || "",
              agencyCodeSetCode: location.agencyCodeSetCode || "",
              code: location.code || "",
              countryCode: location.countryCode || "",
              subentityCode: location.subentityCode || "",
              supraentityCode: location.supraentityCode || "",
              description: location.description || "",
              allocationPercentage: {
                value: location.allocationPercentage?.value || 0,
                unitCode: location.allocationPercentage?.unitCode || "",
              },
              ruleCode: location.ruleCode || "",
            })),
            classifications: (provision.classifications || []).map((cls) => ({
              type: cls.type || "",
              agencyCode: cls.agencyCode || "",
              agencyCodeSetCode: cls.agencyCodeSetCode || "",
              description: cls.description || "",
              code: cls.code || "",
              ruleCode: cls.ruleCode || "",
            })),
            amounts: (provision.amounts || []).map((amount) => ({
              type: amount.type || "",
              unitCode: amount.unitCode || "",
              ccy: amount.ccy || "",
              value: amount.value || 0,
              description: amount.description || "",
            })),
            dateTimes: (provision.dateTimes || []).map((dateTime) => ({
              type: dateTime.type || "",
              format: dateTime.format || "",
              timeZoneCode: dateTime.timeZoneCode || "",
              value: dateTime.value || "",
              description: dateTime.description || "",
            })),
            allocations: (provision.allocations || []).map((allocation) => ({
              type: allocation.type || "",
              description: allocation.description || "",
              code: allocation.code || "",
              value: allocation.value || 0,
              reference: allocation.reference || "",
            })),
          })
        ),
      })),
    });
  }, [contractInstance, productTemplate]);

  const updateField = (field, value) => {
    setPlacement({ ...placement, [field]: value });
  };

  const updateContractField = (field, value) => {
    setPlacement({
      ...placement,
      contract: {
        ...placement.contract,
        [field]: value,
      },
    });
  };

  const updateSection = (index, field, value) => {
    const newSections = [...placement.sections];
    newSections[index][field] = value;
    setPlacement({ ...placement, sections: newSections });
  };

  const handleSave = async () => {
    if (!contractInstance || !contractInstance._id) {
      toast({ title: "Missing contract ID. Cannot save.", status: "error" });
      return;
    }

    const fullPayload = {
      ...contractInstance,
      grlcPlacement: placement,
    };

    try {
      const response = await fetch(
        `https://poc.mrc.ai/api/deals/${contractInstance._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fullPayload),
        }
      );

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Save failed");
      }

      toast({ title: "Contract saved successfully", status: "success" });
    } catch (err) {
      toast({
        title: "Error saving contract",
        description: err.message,
        status: "error",
      });
      console.error("Save error:", err);
    }
  };

  return (
    <Box p={6} maxWidth="100%" mx="auto">
      <HStack>
        <Tooltip
          label="Back to the Product Dashboard"
          aria-label="Product Dashboard tooltip">
          <IconButton
            icon={<HiOutlineArrowNarrowLeft />}
            size="md"
            onClick={() => window.history.back()}
          />
        </Tooltip>
        <Heading size="lg">Edit Placement Data</Heading>
        <Tooltip label="Save Placement Data" aria-label="Save tooltip">
          <IconButton icon={<HiOutlineSave />} size="md" onClick={handleSave} />
        </Tooltip>
      </HStack>

      <Flex gap={6}>
        <Box mb={4} flex="3">
          <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>Contract Header</Tab>
              <Tab>References</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor={"gray.500"}>
                  <SimpleGrid columns={1} spacing={4} mt={4}>
                    <UuidPreview uuid={placement.contract._id} />
                    <FormControl>
                      <FormLabel>Name</FormLabel>
                      <Input
                        value={placement.contract.name}
                        onChange={(e) =>
                          updateContractField("name", e.target.value)
                        }
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                    <CodesetSelect
                      label="Placing Stage"
                      codesetCode="A83"
                      filter="#Placing"
                      value={
                        placement.placingStageCode
                          ? {
                              code: placement.placingStageCode,
                              description: "",
                            }
                          : null
                      }
                      onChange={(value) =>
                        updateField("placingStageCode", value.code)
                      }
                    />

                    <CodesetSelect
                      label="Placing Transaction"
                      codesetCode="1225"
                      filter="#Placing"
                      value={
                        placement.placingTransactionFunctionCode
                          ? {
                              code: placement.placingTransactionFunctionCode,
                              description: "",
                            }
                          : null
                      }
                      onChange={(value) =>
                        updateField(
                          "placingTransactionFunctionCode",
                          value.code
                        )
                      }
                    />
                  </SimpleGrid>

                  <SimpleGrid columns={1} spacing={4} mt={4}>
                    <FormControl>
                      <FormLabel>Type Description</FormLabel>
                      <Input
                        value={placement.contract.typeDescription || ""}
                        onChange={(e) =>
                          updateContractField("typeDescription", e.target.value)
                        }
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mt={4}>
                    <CodesetSelect
                      label="Type"
                      codesetCode="A82"
                      value={
                        placement.contract.type
                          ? { code: placement.contract.type, description: "" }
                          : null
                      }
                      onChange={(value) =>
                        updateContractField("type", value.code)
                      }
                    />

                    <CodesetSelect
                      label="Nature"
                      codesetCode="A33"
                      value={
                        placement.contract.natureCode
                          ? {
                              code: placement.contract.natureCode,
                              description: "",
                            }
                          : null
                      }
                      onChange={(value) =>
                        updateContractField("natureCode", value.code)
                      }
                    />
                    <CodesetSelect
                      label="Treaty/Fac"
                      codesetCode="A29"
                      value={
                        placement.contract.treatyFacCode
                          ? {
                              code: placement.contract.treatyFacCode,
                              description: "",
                            }
                          : null
                      }
                      onChange={(value) =>
                        updateContractField("treatyFacCode", value.code)
                      }
                    />
                    <CodesetSelect
                      label="Accounting Basis"
                      codesetCode="A113"
                      value={
                        placement.contract.accountingBasis
                          ? {
                              code: placement.contract.accountingBasis,
                              description: "",
                            }
                          : null
                      }
                      onChange={(value) =>
                        updateContractField("accountingBasis", value.code)
                      }
                    />
                  </SimpleGrid>
                </Box>
              </TabPanel>

              <TabPanel>
                <ReferencesEditor
                  references={placement.contract.references || []}
                  onChange={(newRefs) =>
                    setPlacement({
                      ...placement,
                      contract: {
                        ...placement.contract,
                        references: newRefs,
                      },
                    })
                  }
                />
              </TabPanel>
            </TabPanels>
          </Tabs>

          <Box
            borderWidth="1px"
            borderRadius="md"
            p={4}
            borderColor={"gray.500"}>
            <Heading size="md" mb={4}>
              Sections
            </Heading>
            <Tabs variant="solid-rounded" colorScheme="linkedin" isLazy>
              <TabList>
                {placement.sections.map((section, idx) => (
                  <Tab key={idx}>Section {idx + 1}</Tab>
                ))}

                <IconButton
                  icon={<HiOutlinePlus />}
                  size="md"
                  ml={4}
                  onClick={() =>
                    setPlacement({
                      ...placement,
                      sections: [
                        ...placement.sections,
                        {
                          _id: crypto.randomUUID(),
                          name: "",
                          description: "",
                          contractReportingLevelCode: "",
                          riskOrInterestDescription: "",
                          type: "",
                          natureCode: "",
                          treatyFacCode: "",
                          coverType: "",
                          coverOperatingBasis: "",
                          contractAmountsCcy: "",
                          references: [],
                          period: {
                            id: crypto.randomUUID(),
                            statusCode: "",
                            description: "",
                            scopeCode: "",
                            extensionProvisionsDescription: "",
                            startDateTime: { value: "", timeZoneCode: "" },
                            endDateTime: { value: "", timeZoneCode: "" },
                            anniversaryDateTime: "",
                            underwritingYear: "",
                          },
                          lineBasis: { writtenBasis: "", signedBasis: "" },
                          orderShare: {
                            amount: { ccy: "", value: 0 },
                            percentage: { value: 0, unitCode: "" },
                          },
                          brokerShare: {
                            amount: { ccy: "", value: 0 },
                            percentage: { value: 0, unitCode: "" },
                          },
                          claimsMadeProvisions: {
                            continuityDateTime: "",
                            pendingAndPriorLitigationDateTime: "",
                            coverageRetroactiveDateTime: "",
                          },
                          classes: [
                            {
                              id: crypto.randomUUID(),
                              agencyCode: "",
                              agencyCodeSetCode: "",
                              code: "",
                              description: "",
                              allocationPercentage: { value: 0, unitCode: "" },
                            },
                          ],
                          perils: [
                            {
                              id: crypto.randomUUID(),
                              agencyCode: "",
                              agencyCodeSetCode: "",
                              code: "",
                              description: "",
                              allocationPercentage: { value: 0, unitCode: "" },
                            },
                          ],
                          locations: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              agencyCode: "",
                              agencyCodeSetCode: "",
                              code: "",
                              countryCode: "",
                              subentityCode: "",
                              supraentityCode: "",
                              description: "",
                              allocationPercentage: { value: 0, unitCode: "" },
                              ruleCode: "",
                            },
                          ],
                          coverages: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              description: "",
                              amount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                              percentage: { value: 0, unitCode: "" },
                              basis: [
                                {
                                  code: "",
                                  operation: "",
                                  subject: "",
                                  description: "",
                                },
                              ],
                              numberOfLines: 0,
                              numberOfUnits: 0,
                            },
                          ],
                          deductibles: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              description: "",
                              amount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                              percentage: { value: 0, unitCode: "" },
                              basis: [
                                {
                                  code: "",
                                  operation: "",
                                  subject: "",
                                  description: "",
                                },
                              ],
                              numberOfLines: 0,
                              numberOfUnits: 0,
                            },
                          ],
                          premiums: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              description: "",
                              amount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                              adjustableIndicator: "",
                              rate: { value: 0, unitCode: "" },
                              minimumRate: { value: 0, unitCode: "" },
                              maximumRate: { value: 0, unitCode: "" },
                              incomeAmountBasis: "",
                              termsOfTradePeriodDays: 0,
                              numberOfInstalments: 0,
                              instalments: [
                                {
                                  id: crypto.randomUUID(),
                                  sequenceNbr: 0,
                                  amount: {
                                    shareCode: "",
                                    ccyIndicator: "",
                                    ccy: "",
                                    value: 0,
                                  },
                                  percentage: { value: 0, unitCode: "" },
                                  rate: { value: 0, unitCode: "" },
                                  minimumRate: { value: 0, unitCode: "" },
                                  maximumRate: { value: 0, unitCode: "" },
                                  incomeAmountBasis: "",
                                  dueDateTime: {
                                    timeZoneCode: "",
                                    qualifier: "",
                                    estimatedIndicator: "",
                                    value: "",
                                  },
                                  termsOfTradePeriodDuration: 0,
                                  termsOfTradePeriodBasis: "",
                                  payableByDateTime: {
                                    timeZoneCode: "",
                                    qualifier: "",
                                    estimatedIndicator: "",
                                    value: "",
                                  },
                                  description: "",
                                },
                              ],
                            },
                          ],
                          premiumAdjustment: {
                            id: crypto.randomUUID(),
                            description: "",
                            creditPeriodDurationDays: 0,
                            premiumAmountCurrencyCode: "",
                            premiumAmount: {
                              shareCode: "",
                              ccyIndicator: "",
                              ccy: "",
                              value: 0,
                            },
                            rate: { value: 0, unitCode: "" },
                            minimumRate: { value: 0, unitCode: "" },
                            maximumRate: { value: 0, unitCode: "" },
                            basis: "",
                            calculationType: "",
                            calculationLoadingOperationCode: "",
                            calculationLoadingFractionNumerator: 0,
                            calculationLoadingFractionDenominator: 0,
                            calculationFrequencyCode: "",
                            calculationPeriodStartDateTime: {
                              value: "",
                              timeZoneCode: "",
                            },
                            calculationPeriodEndDateTime: {
                              value: "",
                              timeZoneCode: "",
                            },
                          },
                          taxes: [
                            {
                              id: crypto.randomUUID(),
                              description: "",
                              applicationType: "",
                              classCode: "",
                              type: "",
                              locations: [
                                {
                                  id: crypto.randomUUID(),
                                  type: "",
                                  agencyCode: "",
                                  agencyCodeSetCode: "",
                                  code: "",
                                  countryCode: "",
                                  subentityCode: "",
                                  supraentityCode: "",
                                  description: "",
                                  allocationPercentage: {
                                    value: 0,
                                    unitCode: "",
                                  },
                                  ruleCode: "",
                                },
                              ],
                              rateBasis: "",
                              payableByParty: { type: "", id: "" },
                              administratorParty: { type: "", id: "" },
                              baseAmount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                              rate: { value: 0, unitCode: "" },
                              unitCount: 0,
                              amount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                            },
                          ],
                          commissions: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              description: "",
                              classCode: "",
                              basis: "",
                              interpolationBasis: "",
                              lossRatioBasis: "",
                              carryForwardCode: "",
                              carryForwardIntervalPeriodType: "",
                              carryForwardInterval: 0,
                              calculationType: "",
                              calculationFrequencyCode: "",
                              firstCalculationDateTime: {
                                value: "",
                                timeZoneCode: "",
                              },
                              calculationStartDateTime: {
                                value: "",
                                timeZoneCode: "",
                              },
                              calculationEndDateTime: {
                                value: "",
                                timeZoneCode: "",
                              },
                              managementExpensesPercentage: {
                                value: 0,
                                unitCode: "",
                              },
                              payableToParty: { type: "", id: "" },
                              payableByParty: { type: "", id: "" },
                              commissionValues: [
                                {
                                  basis: "",
                                  sequenceNbr: 0,
                                  percentage: { value: 0, unitCode: "" },
                                  lossRatioPercentage: {
                                    value: 0,
                                    unitCode: "",
                                  },
                                  cedingCommissionPercentage: {
                                    value: 0,
                                    unitCode: "",
                                  },
                                  amount: {
                                    shareCode: "",
                                    ccyIndicator: "",
                                    ccy: "",
                                    value: 0,
                                  },
                                },
                              ],
                            },
                          ],
                          otherDeductions: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              description: "",
                              classCode: "",
                              percentage: { value: 0, unitCode: "" },
                              basis: "",
                              amount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                              payableToParty: { type: "", id: "" },
                              payableByParty: { type: "", id: "" },
                              serviceProvidedToParty: { type: "", id: "" },
                            },
                          ],
                          brokerages: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              description: "",
                              classCode: "",
                              percentage: { value: 0, unitCode: "" },
                              basis: "",
                              amount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                              baseAmount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                              payableToParty: { type: "", id: "" },
                              payableByParty: { type: "", id: "" },
                            },
                          ],
                          reinstatements: [
                            {
                              id: crypto.randomUUID(),
                              sequenceNbr: 0,
                              description: "",
                              unlimitedReinstatementsIndicator: "",
                              numberOfReinstatements: 0,
                              brokeragePercentage: { value: 0, unitCode: "" },
                              brokerageAmount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                              calculationType: "",
                              premiumRate: { value: 0, unitCode: "" },
                              premiumAmount: {
                                shareCode: "",
                                ccyIndicator: "",
                                ccy: "",
                                value: 0,
                              },
                            },
                          ],
                          contractParties: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              roleCode: "",
                              participantFunctionCode: "",
                              party: {
                                _id: crypto.randomUUID(),
                                name: "",
                                type: "",
                                roleCode: "",
                                partyIdentities: [
                                  {
                                    id: "",
                                    agencyCode: "",
                                  },
                                ],
                                address: {
                                  type: "",
                                  numberAndStreet: "",
                                  subentityCode: "",
                                  cityName: "",
                                  postalCode: "",
                                  countryCode: "",
                                },
                                contact: {
                                  description: "",
                                  roleCode: "",
                                  personName: "",
                                  telephone: "",
                                  fax: "",
                                  email: "",
                                },
                              },
                              references: [
                                {
                                  ownerCode: "",
                                  type: "",
                                  value: "",
                                },
                              ],
                              classifications: [
                                {
                                  type: "",
                                  agencyCode: "",
                                  agencyCodeSetCode: "",
                                  description: "",
                                  code: "",
                                  ruleCode: "",
                                },
                              ],
                            },
                          ],
                          contractMarkets: [
                            {
                              id: crypto.randomUUID(),
                              party: {
                                _id: crypto.randomUUID(),
                                name: "",
                                type: "",
                                roleCode: "",
                                partyIdentities: [
                                  {
                                    id: "",
                                    agencyCode: "",
                                  },
                                ],
                                address: {
                                  type: "",
                                  numberAndStreet: "",
                                  subentityCode: "",
                                  cityName: "",
                                  postalCode: "",
                                  countryCode: "",
                                },
                                contact: {
                                  description: "",
                                  roleCode: "",
                                  personName: "",
                                  telephone: "",
                                  fax: "",
                                  email: "",
                                },
                              },
                              participationBasis: "",
                              references: [
                                {
                                  ownerCode: "",
                                  type: "",
                                  value: "",
                                },
                              ],
                              writtenDateTime: "",
                              participantFunctionCode: "",
                              endorsementParticipantFunctionCode: "",
                              claimParticipantFunctionCode: "",
                              declarationParticipantFunctionCode: "",
                              lineToStandIndicator: "",
                              minimumShare: 0,
                              maximumShare: 0,
                              quotedShare: 0,
                              writtenShare: 0,
                              signedShare: 0,
                              classifications: [
                                {
                                  type: "",
                                  agencyCode: "",
                                  agencyCodeSetCode: "",
                                  description: "",
                                  code: "",
                                  ruleCode: "",
                                },
                              ],
                            },
                          ],
                          schedules: [
                            {
                              id: crypto.randomUUID(),
                              type: "",
                              description: "",
                              scheduleEntries: [
                                {
                                  id: crypto.randomUUID(),
                                  type: "",
                                  classCode: "",
                                  parentId: "",
                                  parentType: "",
                                  parentClassCode: "",
                                  metadataStandardCode: "",
                                  name: "",
                                  description: "",
                                  quantity: 0,
                                  parties: [
                                    {
                                      type: "",
                                      id: crypto.randomUUID(),
                                      name: "",
                                      roleCode: "",
                                      partyIdentities: [
                                        {
                                          id: "",
                                          agencyCode: "",
                                        },
                                      ],
                                      address: {
                                        type: "",
                                        numberAndStreet: "",
                                        subentityCode: "",
                                        cityName: "",
                                        postalCode: "",
                                        countryCode: "",
                                      },
                                      contact: {
                                        description: "",
                                        roleCode: "",
                                        personName: "",
                                        telephone: "",
                                        fax: "",
                                        email: "",
                                      },
                                    },
                                  ],
                                  references: [
                                    {
                                      ownerCode: "",
                                      type: "",
                                      value: "",
                                    },
                                  ],
                                  classifications: [
                                    {
                                      type: "",
                                      agencyCode: "",
                                      agencyCodeSetCode: "",
                                      description: "",
                                      code: "",
                                      ruleCode: "",
                                    },
                                  ],
                                  amounts: [
                                    {
                                      type: "",
                                      unitCode: "",
                                      ccy: "",
                                      value: 0,
                                      description: "",
                                    },
                                  ],
                                  dateTimes: [
                                    {
                                      type: "",
                                      value: "",
                                      timeZoneCode: "",
                                      qualifier: "",
                                      estimatedIndicator: "",
                                    },
                                  ],
                                  text: [
                                    {
                                      type: "",
                                      agencyCode: "",
                                      agencyCodeSetCode: "",
                                      reference: "",
                                      name: "",
                                      value: "",
                                    },
                                  ],
                                  coverageAttachments: [
                                    {
                                      value: "",
                                    },
                                  ],
                                  clauseAttachments: [
                                    {
                                      value: "",
                                    },
                                  ],
                                },
                              ],
                              entryRules: [
                                {
                                  genericType: "",
                                  type: "",
                                  requirement: "",
                                  unlimited: "",
                                  allowedCount: 0,
                                },
                              ],
                            },
                          ],
                          regulatoryProvisions: [
                            {
                              id: crypto.randomUUID(),
                              regulatoryRiskLocations: [
                                {
                                  id: crypto.randomUUID(),
                                  type: "",
                                  agencyCode: "",
                                  agencyCodeSetCode: "",
                                  code: "",
                                  countryCode: "",
                                  subentityCode: "",
                                  supraentityCode: "",
                                  description: "",
                                  allocationPercentage: {
                                    value: 0,
                                    unitCode: "",
                                  },
                                  ruleCode: "",
                                },
                              ],
                              classifications: [
                                {
                                  type: "",
                                  agencyCode: "",
                                  agencyCodeSetCode: "",
                                  description: "",
                                  code: "",
                                  ruleCode: "",
                                },
                              ],
                              amounts: [
                                {
                                  type: "",
                                  unitCode: "",
                                  ccy: "",
                                  value: 0,
                                  description: "",
                                },
                              ],
                              dateTimes: [
                                {
                                  type: "",
                                  format: "",
                                  timeZoneCode: "",
                                  value: "",
                                  description: "",
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    })
                  }
                />
              </TabList>
              <TabPanels>
                {placement.sections.map((section, idx) => (
                  <TabPanel key={idx}>
                    <Tabs
                      orientation="vertical"
                      variant="line"
                      colorScheme="linkedin">
                      <TabList>
                        <Tab>Header</Tab>
                        <Tab>Classes</Tab>
                        <Tab>Locations</Tab>
                        <Tab>Perils</Tab>
                        <Tab>Coverages</Tab>
                        <Tab>Deductibles</Tab>
                        <Tab>Premium</Tab>
                        <Tab>Premium Adjustment</Tab>
                        <Tab>Tax</Tab>
                        <Tab>Commissions</Tab>
                        <Tab>Other Deductions</Tab>
                        <Tab>Brokerage</Tab>
                        <Tab>Reinstatements</Tab>
                        <Tab>Contract Parties</Tab>
                        <Tab>Contract Market</Tab>
                        <Tab>Clauses</Tab>
                        <Tab>Schedules</Tab>
                        <Tab>Regulatory Provisions</Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel>
                          <UuidPreview uuid={section._id} />
                          <br />

                          {/* HEADER + PERIOD side by side */}
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Box
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              borderColor={"gray.500"}>
                              <Tooltip
                                label={section._id}
                                aria-label="Full UUID">
                                <Heading size="md" mb={4}>
                                  Header
                                </Heading>
                              </Tooltip>

                              <CodesetSelect
                                label="Reporting Level"
                                codesetCode="7495"
                                value={
                                  section.contractReportingLevelCode
                                    ? {
                                        code: section.contractReportingLevelCode,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(
                                    idx,
                                    "contractReportingLevelCode",
                                    value.code
                                  )
                                }
                              />
                              <FormControl>
                                <FormLabel>Name</FormLabel>
                                <Input
                                  value={section.name || ""}
                                  onChange={(e) =>
                                    updateSection(idx, "name", e.target.value)
                                  }
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                  value={section.description || ""}
                                  onChange={(e) =>
                                    updateSection(
                                      idx,
                                      "description",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel>
                                  Risk / Interest Description
                                </FormLabel>
                                <Input
                                  value={
                                    section.riskOrInterestDescription || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(
                                      idx,
                                      "riskOrInterestDescription",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormControl>
                              {/* Additional Fields */}
                              <CodesetSelect
                                label="Type"
                                codesetCode="A82"
                                value={
                                  section.type
                                    ? { code: section.type, description: "" }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "type", value.code)
                                }
                              />
                              <CodesetSelect
                                label="Nature"
                                codesetCode="A33"
                                value={
                                  section.natureCode
                                    ? {
                                        code: section.natureCode,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "natureCode", value.code)
                                }
                              />
                              <CodesetSelect
                                label="Treaty/Fac"
                                codesetCode="A29"
                                value={
                                  section.treatyFacCode
                                    ? {
                                        code: section.treatyFacCode,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(
                                    idx,
                                    "treatyFacCode",
                                    value.code
                                  )
                                }
                              />
                              <CodesetSelect
                                label="Cover Type"
                                codesetCode="A31"
                                value={
                                  section.coverType
                                    ? {
                                        code: section.coverType,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "coverType", value.code)
                                }
                              />
                              <CodesetSelect
                                label="Cover Operating Basis"
                                codesetCode="A15"
                                value={
                                  section.coverOperatingBasis
                                    ? {
                                        code: section.coverOperatingBasis,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(
                                    idx,
                                    "coverOperatingBasis",
                                    value.code
                                  )
                                }
                              />
                              <FormControl>
                                <FormLabel>Contract Amounts Ccy</FormLabel>
                                <Input
                                  value={section.contractAmountsCcy || ""}
                                  onChange={(e) =>
                                    updateSection(
                                      idx,
                                      "contractAmountsCcy",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormControl>
                            </Box>

                            <Box
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              borderColor={"gray.500"}>
                              <Heading size="md" mb={4}>
                                Period
                              </Heading>

                              <CodesetSelect
                                label="Status"
                                codesetCode="A140"
                                value={
                                  section.period.statusCode
                                    ? {
                                        code: section.period.statusCode,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "period", {
                                    ...section.period,
                                    statusCode: value.code,
                                  })
                                }
                              />

                              <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                  value={section.period.description || ""}
                                  onChange={(e) =>
                                    updateSection(idx, "period", {
                                      ...section.period,
                                      description: e.target.value,
                                    })
                                  }
                                />
                              </FormControl>

                              <CodesetSelect
                                label="Scope"
                                codesetCode="A138"
                                value={
                                  section.period.scopeCode
                                    ? {
                                        code: section.period.scopeCode,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "period", {
                                    ...section.period,
                                    scopeCode: value.code,
                                  })
                                }
                              />

                              <FormControl>
                                <FormLabel>
                                  Extension Provisions Description
                                </FormLabel>
                                <Input
                                  value={
                                    section.period
                                      .extensionProvisionsDescription || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(idx, "period", {
                                      ...section.period,
                                      extensionProvisionsDescription:
                                        e.target.value,
                                    })
                                  }
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel>Inception</FormLabel>
                                <Input
                                  value={
                                    section.period.startDateTime.value || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(idx, "period", {
                                      ...section.period,
                                      startDateTime: {
                                        ...section.period.startDateTime,
                                        value: e.target.value,
                                      },
                                    })
                                  }
                                />
                              </FormControl>

                              <CodesetSelect
                                label="StartDate Time Zone"
                                codesetCode="B39"
                                value={
                                  section.period.startDateTime.timeZoneCode
                                    ? {
                                        code: section.period.startDateTime
                                          .timeZoneCode,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "period", {
                                    ...section.period,
                                    startDateTime: {
                                      ...section.period.startDateTime,
                                      timeZoneCode: value.code,
                                    },
                                  })
                                }
                              />

                              <FormControl>
                                <FormLabel>Expiry</FormLabel>
                                <Input
                                  value={section.period.endDateTime.value || ""}
                                  onChange={(e) =>
                                    updateSection(idx, "period", {
                                      ...section.period,
                                      endDateTime: {
                                        ...section.period.endDateTime,
                                        value: e.target.value,
                                      },
                                    })
                                  }
                                />
                              </FormControl>

                              <CodesetSelect
                                label="EndDate Time Zone"
                                codesetCode="B39"
                                value={
                                  section.period.endDateTime.timeZoneCode
                                    ? {
                                        code: section.period.endDateTime
                                          .timeZoneCode,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "period", {
                                    ...section.period,
                                    endDateTime: {
                                      ...section.period.endDateTime,
                                      timeZoneCode: value.code,
                                    },
                                  })
                                }
                              />

                              <FormControl>
                                <FormLabel>Anniversary Date</FormLabel>
                                <Input
                                  value={
                                    section.period.anniversaryDateTime || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(idx, "period", {
                                      ...section.period,
                                      anniversaryDateTime: e.target.value,
                                    })
                                  }
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel>Underwriting Year</FormLabel>
                                <Input
                                  value={section.period.underwritingYear || ""}
                                  onChange={(e) =>
                                    updateSection(idx, "period", {
                                      ...section.period,
                                      underwritingYear: e.target.value,
                                    })
                                  }
                                />
                              </FormControl>
                            </Box>
                          </SimpleGrid>

                          {/* Shares */}
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Box
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              borderColor={"gray.500"}>
                              <Heading size="md" mb={4}>
                                Order
                              </Heading>

                              <FormControl>
                                <FormLabel>Share%</FormLabel>
                                <Input
                                  value={
                                    section.orderShare?.percentage?.value || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(idx, "orderShare", {
                                      ...section.orderShare,
                                      percentage: {
                                        ...section.orderShare?.percentage,
                                        value: e.target.value,
                                      },
                                    })
                                  }
                                />
                              </FormControl>

                              <CurrencyAmountInput
                                label="Amount"
                                value={
                                  section.orderShare?.amount || {
                                    ccy: "",
                                    value: 0,
                                  }
                                }
                                onChange={(value) =>
                                  updateSection(idx, "orderShare", {
                                    ...section.orderShare,
                                    amount: value,
                                  })
                                }
                              />
                            </Box>

                            <Box
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              borderColor={"gray.500"}>
                              <Heading size="md" mb={4}>
                                Broker Share
                              </Heading>

                              <FormControl>
                                <FormLabel>Share%</FormLabel>
                                <Input
                                  value={
                                    section.brokerShare?.percentage?.value || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(idx, "brokerShare", {
                                      ...section.brokerShare,
                                      percentage: {
                                        ...section.brokerShare?.percentage,
                                        value: e.target.value,
                                      },
                                    })
                                  }
                                />
                              </FormControl>

                              <CurrencyAmountInput
                                label="Amount"
                                value={
                                  section.brokerShare?.amount || {
                                    ccy: "",
                                    value: 0,
                                  }
                                }
                                onChange={(value) =>
                                  updateSection(idx, "brokerShare", {
                                    ...section.brokerShare,
                                    amount: value,
                                  })
                                }
                              />
                            </Box>

                            <Box
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              borderColor={"gray.500"}>
                              <Heading size="md" mb={4}>
                                Basis Of Line
                              </Heading>

                              <CodesetSelect
                                label="Written Basis"
                                codesetCode="B28"
                                value={
                                  section.lineBasis?.writtenBasis
                                    ? {
                                        code: section.lineBasis.writtenBasis,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "lineBasis", {
                                    ...section.lineBasis,
                                    writtenBasis: value.code,
                                  })
                                }
                              />

                              <CodesetSelect
                                label="Signed Basis"
                                codesetCode="B28"
                                value={
                                  section.lineBasis?.signedBasis
                                    ? {
                                        code: section.lineBasis.signedBasis,
                                        description: "",
                                      }
                                    : null
                                }
                                onChange={(value) =>
                                  updateSection(idx, "lineBasis", {
                                    ...section.lineBasis,
                                    signedBasis: value.code,
                                  })
                                }
                              />
                            </Box>

                            <Box
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              borderColor={"gray.500"}>
                              <Heading size="md" mb={4}>
                                Claims Made Provisions
                              </Heading>

                              <FormControl>
                                <FormLabel>Continuity Date</FormLabel>
                                <Input
                                  value={
                                    section.claimsMadeProvisions
                                      ?.continuityDateTime || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(idx, "claimsMadeProvisions", {
                                      ...section.claimsMadeProvisions,
                                      continuityDateTime: e.target.value,
                                    })
                                  }
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel>
                                  Pending and Prior Litigation Date
                                </FormLabel>
                                <Input
                                  value={
                                    section.claimsMadeProvisions
                                      ?.pendingAndPriorLitigationDateTime || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(idx, "claimsMadeProvisions", {
                                      ...section.claimsMadeProvisions,
                                      pendingAndPriorLitigationDateTime:
                                        e.target.value,
                                    })
                                  }
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel>Coverage Retroactive Date</FormLabel>
                                <Input
                                  value={
                                    section.claimsMadeProvisions
                                      ?.coverageRetroactiveDateTime || ""
                                  }
                                  onChange={(e) =>
                                    updateSection(idx, "claimsMadeProvisions", {
                                      ...section.claimsMadeProvisions,
                                      coverageRetroactiveDateTime:
                                        e.target.value,
                                    })
                                  }
                                />
                              </FormControl>
                            </Box>
                          </SimpleGrid>

                          {/* REFERENCES BELOW */}
                          <Box
                            borderWidth="1px"
                            borderRadius="md"
                            p={4}
                            borderColor={"gray.500"}>
                            <ReferencesEditor
                              references={section.references || []}
                              onChange={(newRefs) =>
                                updateSection(idx, "references", newRefs)
                              }
                            />
                          </Box>
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading verticalAlign={"center"} size="md">
                              Classes Of Business
                            </Heading>
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              verticalAlign="center"
                              mb={4}
                              onClick={() => {
                                const newClasses = [
                                  ...(section.classes || []),
                                  {
                                    id: crypto.randomUUID(),
                                    agencyCode: "",
                                    agencyCodeSetCode: "",
                                    code: "",
                                    description: "",
                                    ruleCode: "",
                                    allocationPercentage: {
                                      value: 0,
                                      unitCode: "",
                                    },
                                  },
                                ];
                                updateSection(idx, "classes", newClasses);
                              }}
                            />
                          </HStack>

                          {(section.classes || []).map((cls, clsIdx) => (
                            <Box
                              key={clsIdx}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={4}
                              borderColor="gray.500">
                              <UuidPreview uuid={cls.id} />

                              {/* Row 2: Full Width Description */}
                              <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                  value={cls.description || ""}
                                  onChange={(e) => {
                                    const newClasses = [...section.classes];
                                    newClasses[clsIdx] = {
                                      ...newClasses[clsIdx],
                                      description: e.target.value,
                                    };
                                    updateSection(idx, "classes", newClasses);
                                  }}
                                />
                              </FormControl>

                              {/* Row 1: 5 Fields Side by Side */}
                              <SimpleGrid
                                columns={{ base: 1, md: 5 }}
                                spacing={4}>
                                <CodesetSelect
                                  label="Agency Code"
                                  codesetCode="3055"
                                  value={
                                    cls.agencyCode
                                      ? {
                                          code: cls.agencyCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newClasses = [...section.classes];
                                    newClasses[clsIdx] = {
                                      ...newClasses[clsIdx],
                                      agencyCode: value.code,
                                    };
                                    updateSection(idx, "classes", newClasses);
                                  }}
                                />
                                <CodesetSelect
                                  label="Agency Code Set"
                                  codesetCode="C01"
                                  filter="#Class"
                                  value={
                                    cls.agencyCodeSetCode
                                      ? {
                                          code: cls.agencyCodeSetCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newClasses = [...section.classes];
                                    newClasses[clsIdx] = {
                                      ...newClasses[clsIdx],
                                      agencyCodeSetCode: value.code,
                                      agencyCode: value.codeListAgency, // ✅ add this
                                    };
                                    updateSection(idx, "classes", newClasses);
                                  }}
                                />

                                <CodesetSelect
                                  label="Code"
                                  codesetCode={cls.agencyCodeSetCode}
                                  value={
                                    cls.code
                                      ? { code: cls.code, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newClasses = [...section.classes];
                                    newClasses[clsIdx] = {
                                      ...newClasses[clsIdx],
                                      code: value.code,
                                    };
                                    updateSection(idx, "classes", newClasses);
                                  }}
                                />

                                <CodesetSelect
                                  label="Rule Code"
                                  codesetCode="B60"
                                  value={
                                    cls.ruleCode
                                      ? { code: cls.ruleCode, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newClasses = [...section.classes];
                                    newClasses[clsIdx] = {
                                      ...newClasses[clsIdx],
                                      ruleCode: value.code,
                                    };
                                    updateSection(idx, "classes", newClasses);
                                  }}
                                />

                                <FormControl>
                                  <FormLabel>Allocation %</FormLabel>
                                  <Input
                                    type="number"
                                    value={
                                      cls.allocationPercentage?.value || ""
                                    }
                                    onChange={(e) => {
                                      const newClasses = [...section.classes];
                                      newClasses[clsIdx] = {
                                        ...newClasses[clsIdx],
                                        allocationPercentage: {
                                          ...cls.allocationPercentage,
                                          value:
                                            parseFloat(e.target.value) || 0,
                                          unitCode:
                                            cls.allocationPercentage
                                              ?.unitCode || "",
                                        },
                                      };
                                      updateSection(idx, "classes", newClasses);
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              {/* Delete Button */}
                              <HStack justify="flex-end">
                                <IconButton
                                  icon={<HiOutlineTrash />}
                                  size="md"
                                  onClick={() => {
                                    const newClasses = [...section.classes];
                                    newClasses.splice(clsIdx, 1);
                                    updateSection(idx, "classes", newClasses);
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Locations</Heading>
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              mb={4}
                              onClick={() => {
                                const newLocations = [
                                  ...(section.locations || []),
                                  {
                                    id: crypto.randomUUID(),
                                    agencyCode: "",
                                    agencyCodeSetCode: "",
                                    code: "",
                                    description: "",
                                    ruleCode: "",
                                    allocationPercentage: {
                                      value: 0,
                                      unitCode: "",
                                    },
                                  },
                                ];
                                updateSection(idx, "locations", newLocations);
                              }}
                            />
                          </HStack>

                          {(section.locations || []).map((loc, locIdx) => (
                            <Box
                              key={locIdx}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={4}
                              borderColor="gray.500">
                              <UuidPreview uuid={loc.id} />

                              <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                  value={loc.description || ""}
                                  onChange={(e) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      description: e.target.value,
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />
                              </FormControl>

                              <SimpleGrid
                                columns={{ base: 1, md: 6 }}
                                spacing={4}>
                                <CodesetSelect
                                  label="Type"
                                  codesetCode="3227"
                                  filter="#Location"
                                  value={
                                    loc.type
                                      ? { code: loc.type, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      type: value.code,
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />

                                <CodesetSelect
                                  label="Agency Code"
                                  codesetCode="3055"
                                  value={
                                    loc.agencyCode
                                      ? {
                                          code: loc.agencyCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      agencyCode: value.code,
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />
                                <CodesetSelect
                                  label="Agency Code Set"
                                  codesetCode="C01"
                                  filter="#Location"
                                  value={
                                    loc.agencyCodeSetCode
                                      ? {
                                          code: loc.agencyCodeSetCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      agencyCodeSetCode: value.code,
                                      agencyCode: value.codeListAgency, // ✅ this sets loc.agencyCode
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />

                                <CodesetSelect
                                  label="Code"
                                  codesetCode={loc.agencyCodeSetCode}
                                  value={
                                    loc.code
                                      ? { code: loc.code, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      code: value.code,
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />

                                <CodesetSelect
                                  label="Rule Code"
                                  codesetCode="B60"
                                  value={
                                    loc.ruleCode
                                      ? { code: loc.ruleCode, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      ruleCode: value.code,
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />
                                <FormControl>
                                  <FormLabel>Allocation %</FormLabel>
                                  <Input
                                    type="number"
                                    value={
                                      loc.allocationPercentage?.value || ""
                                    }
                                    onChange={(e) => {
                                      const newLocations = [
                                        ...section.locations,
                                      ];
                                      newLocations[locIdx] = {
                                        ...newLocations[locIdx],
                                        allocationPercentage: {
                                          ...loc.allocationPercentage,
                                          value:
                                            parseFloat(e.target.value) || 0,
                                          unitCode:
                                            loc.allocationPercentage
                                              ?.unitCode || "",
                                        },
                                      };
                                      updateSection(
                                        idx,
                                        "locations",
                                        newLocations
                                      );
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 5 }}
                                spacing={4}>
                                <CodesetSelect
                                  label="Country Code"
                                  codesetCode="ISO3166"
                                  value={
                                    loc.countryCode
                                      ? {
                                          code: loc.countryCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      countryCode: value.code,
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />
                                <CodesetSelect
                                  label="Subentity Code"
                                  codesetCode="A53"
                                  value={
                                    loc.subentityCode
                                      ? {
                                          code: loc.subentityCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      subentityCode: value.code,
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />
                                <CodesetSelect
                                  label="Supraentity Code"
                                  codesetCode="A38"
                                  value={
                                    loc.supraentityCode
                                      ? {
                                          code: loc.supraentityCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newLocations = [...section.locations];
                                    newLocations[locIdx] = {
                                      ...newLocations[locIdx],
                                      supraentityCode: value.code,
                                    };
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />
                              </SimpleGrid>

                              <HStack justify="flex-end">
                                <IconButton
                                  icon={<HiOutlineTrash />}
                                  size="md"
                                  onClick={() => {
                                    const newLocations = [...section.locations];
                                    newLocations.splice(locIdx, 1);
                                    updateSection(
                                      idx,
                                      "locations",
                                      newLocations
                                    );
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Perils</Heading>
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              mb={4}
                              onClick={() => {
                                const newPerils = [
                                  ...(section.perils || []),
                                  {
                                    id: crypto.randomUUID(),
                                    agencyCode: "",
                                    agencyCodeSetCode: "",
                                    code: "",
                                    description: "",
                                    ruleCode: "",
                                    allocationPercentage: {
                                      value: 0,
                                      unitCode: "",
                                    },
                                  },
                                ];
                                updateSection(idx, "perils", newPerils);
                              }}
                            />
                          </HStack>

                          {(section.perils || []).map((peril, perilIdx) => (
                            <Box
                              key={perilIdx}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={4}
                              borderColor="gray.500">
                              <UuidPreview uuid={peril.id} />

                              <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                  value={peril.description || ""}
                                  onChange={(e) => {
                                    const newPerils = [...section.perils];
                                    newPerils[perilIdx] = {
                                      ...newPerils[perilIdx],
                                      description: e.target.value,
                                    };
                                    updateSection(idx, "perils", newPerils);
                                  }}
                                />
                              </FormControl>

                              <SimpleGrid
                                columns={{ base: 1, md: 5 }}
                                spacing={4}>
                                <CodesetSelect
                                  label="Agency Code"
                                  codesetCode="3055"
                                  value={
                                    peril.agencyCode
                                      ? {
                                          code: peril.agencyCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newPerils = [...section.perils];
                                    newPerils[perilIdx] = {
                                      ...newPerils[perilIdx],
                                      agencyCode: value.code,
                                    };
                                    updateSection(idx, "perils", newPerils);
                                  }}
                                />
                                <CodesetSelect
                                  label="Agency Code Set"
                                  filter="#Peril"
                                  codesetCode="C01"
                                  value={
                                    peril.agencyCodeSetCode
                                      ? {
                                          code: peril.agencyCodeSetCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newPerils = [...section.perils];
                                    newPerils[perilIdx] = {
                                      ...newPerils[perilIdx],
                                      agencyCodeSetCode: value.code,
                                      agencyCode: value.codeListAgency, // ✅ also update agencyCode
                                    };
                                    updateSection(idx, "perils", newPerils);
                                  }}
                                />

                                <CodesetSelect
                                  label="Code"
                                  codesetCode={peril.agencyCodeSetCode}
                                  value={
                                    peril.code
                                      ? { code: peril.code, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newPerils = [...section.perils];
                                    newPerils[perilIdx] = {
                                      ...newPerils[perilIdx],
                                      code: value.code,
                                    };
                                    updateSection(idx, "perils", newPerils);
                                  }}
                                />
                                <CodesetSelect
                                  label="Rule Code"
                                  codesetCode="B60"
                                  value={
                                    peril.ruleCode
                                      ? {
                                          code: peril.ruleCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newPerils = [...section.perils];
                                    newPerils[perilIdx] = {
                                      ...newPerils[perilIdx],
                                      ruleCode: value.code,
                                    };
                                    updateSection(idx, "perils", newPerils);
                                  }}
                                />
                                <FormControl>
                                  <FormLabel>Allocation %</FormLabel>
                                  <Input
                                    type="number"
                                    value={
                                      peril.allocationPercentage?.value || ""
                                    }
                                    onChange={(e) => {
                                      const newPerils = [...section.perils];
                                      newPerils[perilIdx] = {
                                        ...newPerils[perilIdx],
                                        allocationPercentage: {
                                          ...peril.allocationPercentage,
                                          value:
                                            parseFloat(e.target.value) || 0,
                                          unitCode:
                                            peril.allocationPercentage
                                              ?.unitCode || "",
                                        },
                                      };
                                      updateSection(idx, "perils", newPerils);
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <HStack justify="flex-end">
                                <IconButton
                                  icon={<HiOutlineTrash />}
                                  size="md"
                                  onClick={() => {
                                    const newPerils = [...section.perils];
                                    newPerils.splice(perilIdx, 1);
                                    updateSection(idx, "perils", newPerils);
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Coverages</Heading>
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              mb={4}
                              onClick={() => {
                                const newCoverages = [
                                  ...(section.coverages || []),
                                  {
                                    id: crypto.randomUUID(),
                                    type: "",
                                    description: "",
                                    amount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                    percentage: {
                                      value: 0,
                                      unitCode: "",
                                    },
                                    basis: [
                                      {
                                        code: "",
                                        operation: "",
                                        subject: "",
                                        description: "",
                                      },
                                    ],
                                    numberOfLines: 0,
                                    numberOfUnits: 0,
                                  },
                                ];
                                updateSection(idx, "coverages", newCoverages);
                              }}
                            />
                          </HStack>

                          {(section.coverages || []).map((cov, covIdx) => (
                            <Box
                              key={covIdx}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={4}
                              borderColor="gray.500">
                              <UuidPreview uuid={cov.id} />

                              <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                  value={cov.description}
                                  onChange={(e) => {
                                    const newCoverages = [...section.coverages];
                                    newCoverages[covIdx].description =
                                      e.target.value;
                                    updateSection(
                                      idx,
                                      "coverages",
                                      newCoverages
                                    );
                                  }}
                                />
                              </FormControl>

                              <SimpleGrid
                                columns={{ base: 1, md: 6 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Type"
                                  codesetCode="A86"
                                  value={
                                    cov.type
                                      ? { code: cov.type, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newCoverages = [...section.coverages];
                                    newCoverages[covIdx].type = value.code;
                                    updateSection(
                                      idx,
                                      "coverages",
                                      newCoverages
                                    );
                                  }}
                                />

                                <CurrencyAmountInput
                                  label="Amount"
                                  value={cov.amount}
                                  onChange={(value) => {
                                    const newCoverages = [...section.coverages];
                                    newCoverages[covIdx].amount = value;
                                    updateSection(
                                      idx,
                                      "coverages",
                                      newCoverages
                                    );
                                  }}
                                />
                                <RateAmountInput
                                  label="Rate"
                                  value={cov.percentage}
                                  onChange={(value) => {
                                    const newCoverages = [...section.coverages];
                                    newCoverages[covIdx].percentage = value;
                                    updateSection(
                                      idx,
                                      "coverages",
                                      newCoverages
                                    );
                                  }}
                                />
                                <FormControl>
                                  <FormLabel textAlign="right" width="100px">
                                    #Lines
                                  </FormLabel>
                                  <Input
                                    type="number"
                                    width="100px"
                                    textAlign="right"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck="false"
                                    autoCapitalize="off"
                                    value={cov.numberOfLines}
                                    onChange={(e) => {
                                      const newCoverages = [
                                        ...section.coverages,
                                      ];
                                      newCoverages[covIdx].numberOfLines =
                                        parseInt(e.target.value) || 0;
                                      updateSection(
                                        idx,
                                        "coverages",
                                        newCoverages
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel textAlign="right" width="100px">
                                    #Units
                                  </FormLabel>
                                  <Input
                                    type="number"
                                    width="100px"
                                    textAlign="right"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck="false"
                                    autoCapitalize="off"
                                    value={cov.numberOfUnits}
                                    onChange={(e) => {
                                      const newCoverages = [
                                        ...section.coverages,
                                      ];
                                      newCoverages[covIdx].numberOfUnits =
                                        parseInt(e.target.value) || 0;
                                      updateSection(
                                        idx,
                                        "coverages",
                                        newCoverages
                                      );
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid>
                                <Box
                                  key={covIdx}
                                  borderWidth="1px"
                                  borderRadius="md"
                                  p={4}
                                  mb={4}
                                  borderColor="gray.500">
                                  <FormControl>
                                    <HStack alignItems="center">
                                      <FormLabel>Basis</FormLabel>
                                      <IconButton
                                        icon={<HiOutlinePlus />}
                                        size="sm"
                                        mb={3}
                                        onClick={() => {
                                          const newCoverages = [
                                            ...section.coverages,
                                          ];
                                          newCoverages[covIdx].basis = [
                                            ...(newCoverages[covIdx].basis ||
                                              []),
                                            {
                                              code: "",
                                              operation: "",
                                              subject: "",
                                              description: "",
                                            },
                                          ];
                                          updateSection(
                                            idx,
                                            "coverages",
                                            newCoverages
                                          );
                                        }}
                                      />
                                    </HStack>

                                    {(cov.basis || []).map(
                                      (basis, basisIdx) => (
                                        <HStack
                                          key={basisIdx}
                                          spacing={4}
                                          align="start"
                                          mb={2}>
                                          <CodesetSelect
                                            codesetCode="A93"
                                            value={
                                              basis.code
                                                ? {
                                                    code: basis.code,
                                                    description: "",
                                                  }
                                                : null
                                            }
                                            onChange={(selected) => {
                                              const newCoverages = [
                                                ...section.coverages,
                                              ];
                                              newCoverages[covIdx].basis[
                                                basisIdx
                                              ].code = selected.code;
                                              updateSection(
                                                idx,
                                                "coverages",
                                                newCoverages
                                              );
                                            }}
                                          />
                                          <Input
                                            placeholder="Description"
                                            value={basis.description}
                                            onChange={(e) => {
                                              const newCoverages = [
                                                ...section.coverages,
                                              ];
                                              newCoverages[covIdx].basis[
                                                basisIdx
                                              ].description = e.target.value;
                                              updateSection(
                                                idx,
                                                "coverages",
                                                newCoverages
                                              );
                                            }}
                                          />
                                          <HStack justify="flex-end">
                                            <IconButton
                                              icon={<HiOutlineTrash />}
                                              size="md"
                                              onClick={() => {
                                                const newCoverages = [
                                                  ...section.coverages,
                                                ];
                                                newCoverages[
                                                  covIdx
                                                ].basis.splice(basisIdx, 1);
                                                updateSection(
                                                  idx,
                                                  "coverages",
                                                  newCoverages
                                                );
                                              }}
                                            />
                                          </HStack>
                                        </HStack>
                                      )
                                    )}
                                  </FormControl>
                                </Box>
                              </SimpleGrid>

                              <HStack justify="flex-end">
                                <IconButton
                                  icon={<HiOutlineTrash />}
                                  size="md"
                                  onClick={() => {
                                    const newCoverages = [...section.coverages];
                                    newCoverages.splice(covIdx, 1);
                                    updateSection(
                                      idx,
                                      "coverages",
                                      newCoverages
                                    );
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Deductibles</Heading>
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              mb={4}
                              onClick={() => {
                                const newDeductibles = [
                                  ...(section.deductibles || []),
                                  {
                                    id: crypto.randomUUID(),
                                    type: "",
                                    description: "",
                                    amount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                    percentage: {
                                      value: 0,
                                      unitCode: "",
                                    },
                                    basis: [
                                      {
                                        code: "",
                                        operation: "",
                                        subject: "",
                                        description: "",
                                      },
                                    ],
                                    numberOfLines: 0,
                                    numberOfUnits: 0,
                                  },
                                ];
                                updateSection(
                                  idx,
                                  "deductibles",
                                  newDeductibles
                                );
                              }}
                            />
                          </HStack>

                          {(section.deductibles || []).map((ded, dedIdx) => (
                            <Box
                              key={dedIdx}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={4}
                              borderColor="gray.500">
                              <UuidPreview uuid={ded.id} />

                              <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                  value={ded.description}
                                  onChange={(e) => {
                                    const newDeductibles = [
                                      ...section.deductibles,
                                    ];
                                    newDeductibles[dedIdx].description =
                                      e.target.value;
                                    updateSection(
                                      idx,
                                      "deductibles",
                                      newDeductibles
                                    );
                                  }}
                                />
                              </FormControl>

                              <SimpleGrid
                                columns={{ base: 1, md: 6 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Type"
                                  codesetCode="A87"
                                  value={
                                    ded.type
                                      ? { code: ded.type, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newDeductibles = [
                                      ...section.deductibles,
                                    ];
                                    newDeductibles[dedIdx].type = value.code;
                                    updateSection(
                                      idx,
                                      "deductibles",
                                      newDeductibles
                                    );
                                  }}
                                />

                                <CurrencyAmountInput
                                  label="Amount"
                                  value={ded.amount}
                                  onChange={(value) => {
                                    const newDeductibles = [
                                      ...section.deductibles,
                                    ];
                                    newDeductibles[dedIdx].amount = value;
                                    updateSection(
                                      idx,
                                      "deductibles",
                                      newDeductibles
                                    );
                                  }}
                                />
                                <RateAmountInput
                                  label="Rate"
                                  value={ded.percentage}
                                  onChange={(value) => {
                                    const newDeductibles = [
                                      ...section.deductibles,
                                    ];
                                    newDeductibles[dedIdx].percentage = value;
                                    updateSection(
                                      idx,
                                      "deductibles",
                                      newDeductibles
                                    );
                                  }}
                                />
                                <FormControl>
                                  <FormLabel textAlign="right" width="100px">
                                    #Lines
                                  </FormLabel>
                                  <Input
                                    type="number"
                                    width="100px"
                                    textAlign="right"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    value={ded.numberOfLines}
                                    onChange={(e) => {
                                      const newDeductibles = [
                                        ...section.deductibles,
                                      ];
                                      newDeductibles[dedIdx].numberOfLines =
                                        parseInt(e.target.value) || 0;
                                      updateSection(
                                        idx,
                                        "deductibles",
                                        newDeductibles
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel textAlign="right" width="100px">
                                    #Units
                                  </FormLabel>
                                  <Input
                                    type="number"
                                    width="100px"
                                    textAlign="right"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    value={ded.numberOfUnits}
                                    onChange={(e) => {
                                      const newDeductibles = [
                                        ...section.deductibles,
                                      ];
                                      newDeductibles[dedIdx].numberOfUnits =
                                        parseInt(e.target.value) || 0;
                                      updateSection(
                                        idx,
                                        "deductibles",
                                        newDeductibles
                                      );
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid>
                                <Box
                                  key={dedIdx}
                                  borderWidth="1px"
                                  borderRadius="md"
                                  p={4}
                                  mb={4}
                                  borderColor="gray.500">
                                  <FormControl>
                                    <HStack alignItems="center">
                                      <FormLabel>Basis</FormLabel>
                                      <IconButton
                                        icon={<HiOutlinePlus />}
                                        size="sm"
                                        mb={3}
                                        onClick={() => {
                                          const newDeductibles = [
                                            ...section.deductibles,
                                          ];
                                          newDeductibles[dedIdx].basis = [
                                            ...(newDeductibles[dedIdx].basis ||
                                              []),
                                            {
                                              code: "",
                                              operation: "",
                                              subject: "",
                                              description: "",
                                            },
                                          ];
                                          updateSection(
                                            idx,
                                            "deductibles",
                                            newDeductibles
                                          );
                                        }}
                                      />
                                    </HStack>

                                    {(ded.basis || []).map(
                                      (basis, basisIdx) => (
                                        <HStack
                                          key={basisIdx}
                                          spacing={4}
                                          align="start"
                                          mb={2}>
                                          <CodesetSelect
                                            codesetCode="A91"
                                            value={
                                              basis.code
                                                ? {
                                                    code: basis.code,
                                                    description: "",
                                                  }
                                                : null
                                            }
                                            onChange={(selected) => {
                                              const newDeductibles = [
                                                ...section.deductibles,
                                              ];
                                              newDeductibles[dedIdx].basis[
                                                basisIdx
                                              ].code = selected.code;
                                              updateSection(
                                                idx,
                                                "deductibles",
                                                newDeductibles
                                              );
                                            }}
                                          />
                                          <Input
                                            placeholder="Description"
                                            value={basis.description}
                                            onChange={(e) => {
                                              const newDeductibles = [
                                                ...section.deductibles,
                                              ];
                                              newDeductibles[dedIdx].basis[
                                                basisIdx
                                              ].description = e.target.value;
                                              updateSection(
                                                idx,
                                                "deductibles",
                                                newDeductibles
                                              );
                                            }}
                                          />
                                          <HStack justify="flex-end">
                                            <IconButton
                                              icon={<HiOutlineTrash />}
                                              size="md"
                                              onClick={() => {
                                                const newDeductibles = [
                                                  ...section.deductibles,
                                                ];
                                                newDeductibles[
                                                  dedIdx
                                                ].basis.splice(basisIdx, 1);
                                                updateSection(
                                                  idx,
                                                  "deductibles",
                                                  newDeductibles
                                                );
                                              }}
                                            />
                                          </HStack>
                                        </HStack>
                                      )
                                    )}
                                  </FormControl>
                                </Box>
                              </SimpleGrid>

                              <HStack justify="flex-end">
                                <IconButton
                                  icon={<HiOutlineTrash />}
                                  size="md"
                                  onClick={() => {
                                    const newDeductibles = [
                                      ...section.deductibles,
                                    ];
                                    newDeductibles.splice(dedIdx, 1);
                                    updateSection(
                                      idx,
                                      "deductibles",
                                      newDeductibles
                                    );
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Premiums</Heading>
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              mb={4}
                              onClick={() => {
                                const newPremiums = [
                                  ...(section.premiums || []),
                                  {
                                    id: crypto.randomUUID(),
                                    type: "premium",
                                    description: "New Premium",
                                    amount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                    rate: { value: 0, unitCode: "percentage" },
                                    minimumRate: {
                                      value: 0,
                                      unitCode: "percentage",
                                    },
                                    maximumRate: {
                                      value: 0,
                                      unitCode: "percentage",
                                    },
                                    incomeAmountBasis: "",
                                    termsOfTradePeriodDays: 0,
                                    numberOfInstalments: 0,
                                    instalments: [
                                      {
                                        id: crypto.randomUUID(),
                                        sequenceNbr: 1,
                                        amount: {
                                          shareCode: "",
                                          ccyIndicator: "",
                                          ccy: "",
                                          value: 0,
                                        },
                                        percentage: {
                                          value: 0,
                                          unitCode: "percentage",
                                        },
                                        rate: {
                                          value: 0,
                                          unitCode: "percentage",
                                        },
                                        minimumRate: {
                                          value: 0,
                                          unitCode: "percentage",
                                        },
                                        maximumRate: {
                                          value: 0,
                                          unitCode: "percentage",
                                        },
                                        incomeAmountBasis: "",
                                        dueDateTime: {
                                          value: "",
                                          timeZoneCode: "",
                                        },
                                        termsOfTradePeriodDuration: 0,
                                        termsOfTradePeriodBasis: "",
                                        payableByDateTime: {
                                          value: "",
                                          timeZoneCode: "",
                                        },
                                        description: "",
                                      },
                                    ],
                                  },
                                ];
                                updateSection(idx, "premiums", newPremiums);
                              }}
                            />
                          </HStack>

                          {(section.premiums || []).map(
                            (premium, premiumIdx) => (
                              <Box
                                key={premiumIdx}
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                mb={6}
                                borderColor="gray.500">
                                <UuidPreview uuid={premium.id} />

                                <FormControl>
                                  <FormLabel>Description</FormLabel>
                                  <Input
                                    value={premium.description || ""}
                                    onChange={(e) => {
                                      const newPremiums = [...section.premiums];
                                      newPremiums[premiumIdx].description =
                                        e.target.value;
                                      updateSection(
                                        idx,
                                        "premiums",
                                        newPremiums
                                      );
                                    }}
                                  />
                                </FormControl>

                                <SimpleGrid
                                  columns={{ base: 1, md: 3 }}
                                  spacing={4}
                                  mb={4}>
                                  <CodesetSelect
                                    label="Type"
                                    codesetCode="A85"
                                    value={
                                      premium.type
                                        ? {
                                            code: premium.type,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const newPremiums = [...section.premiums];
                                      newPremiums[premiumIdx].type = value.code;
                                      updateSection(
                                        idx,
                                        "premiums",
                                        newPremiums
                                      );
                                    }}
                                  />

                                  <CurrencyAmountInput
                                    label="Amount"
                                    value={premium.amount}
                                    onChange={(value) => {
                                      const newPremiums = [...section.premiums];
                                      newPremiums[premiumIdx].amount = value;
                                      updateSection(
                                        idx,
                                        "premiums",
                                        newPremiums
                                      );
                                    }}
                                  />
                                </SimpleGrid>

                                <SimpleGrid
                                  columns={{ base: 1, md: 4 }}
                                  spacing={4}
                                  mb={4}>
                                  <RateAmountInput
                                    label="Rate"
                                    value={premium.rate}
                                    onChange={(value) => {
                                      const newPremiums = [...section.premiums];
                                      newPremiums[premiumIdx].rate = value;
                                      updateSection(
                                        idx,
                                        "premiums",
                                        newPremiums
                                      );
                                    }}
                                  />

                                  <RateAmountInput
                                    label="Minimum Rate"
                                    value={premium.minimumRate}
                                    onChange={(value) => {
                                      const newPremiums = [...section.premiums];
                                      newPremiums[premiumIdx].minimumRate =
                                        value;
                                      updateSection(
                                        idx,
                                        "premiums",
                                        newPremiums
                                      );
                                    }}
                                  />

                                  <RateAmountInput
                                    label="Maximum Rate"
                                    value={premium.maximumRate}
                                    onChange={(value) => {
                                      const newPremiums = [...section.premiums];
                                      newPremiums[premiumIdx].maximumRate =
                                        value;
                                      updateSection(
                                        idx,
                                        "premiums",
                                        newPremiums
                                      );
                                    }}
                                  />

                                  {
                                    <CodesetSelect
                                      label="Income Amount Basis"
                                      codesetCode="A90"
                                      value={
                                        premium.incomeAmountBasis
                                          ? {
                                              code: premium.incomeAmountBasis,
                                              description: "",
                                            }
                                          : null
                                      }
                                      onChange={(value) => {
                                        const newPremiums = [
                                          ...section.premiums,
                                        ];
                                        newPremiums[
                                          premiumIdx
                                        ].incomeAmountBasis = value.code;
                                        updateSection(
                                          idx,
                                          "premiums",
                                          newPremiums
                                        );
                                      }}
                                    />
                                  }
                                </SimpleGrid>

                                <SimpleGrid
                                  columns={{ base: 1, md: 2 }}
                                  spacing={4}
                                  mb={4}>
                                  <FormControl>
                                    <FormLabel>
                                      Terms of Trade Period (Days)
                                    </FormLabel>
                                    <Input
                                      type="number"
                                      value={premium.termsOfTradePeriodDays}
                                      onChange={(e) => {
                                        const newPremiums = [
                                          ...section.premiums,
                                        ];
                                        newPremiums[
                                          premiumIdx
                                        ].termsOfTradePeriodDays =
                                          parseInt(e.target.value) || 0;
                                        updateSection(
                                          idx,
                                          "premiums",
                                          newPremiums
                                        );
                                      }}
                                    />
                                  </FormControl>

                                  <FormControl>
                                    <FormLabel>Number of Instalments</FormLabel>
                                    <Input
                                      type="number"
                                      value={premium.numberOfInstalments}
                                      onChange={(e) => {
                                        const newPremiums = [
                                          ...section.premiums,
                                        ];
                                        newPremiums[
                                          premiumIdx
                                        ].numberOfInstalments =
                                          parseInt(e.target.value) || 0;
                                        updateSection(
                                          idx,
                                          "premiums",
                                          newPremiums
                                        );
                                      }}
                                    />
                                  </FormControl>
                                </SimpleGrid>

                                <Box>
                                  <HStack alignItems="center">
                                    <Heading size="sm" mb={2}>
                                      Instalments
                                    </Heading>
                                    <IconButton
                                      icon={<HiOutlinePlus />}
                                      size="md"
                                      mb={4}
                                      onClick={() => {
                                        const newPremiums = [
                                          ...section.premiums,
                                        ];
                                        newPremiums[premiumIdx].instalments = [
                                          ...(newPremiums[premiumIdx]
                                            .instalments || []),
                                          {
                                            id: crypto.randomUUID(),
                                            sequenceNbr: 0,
                                            percentage: {
                                              value: 0,
                                              unitCode: "percentage",
                                            },
                                            amount: {
                                              shareCode: "",
                                              ccyIndicator: "",
                                              ccy: "",
                                              value: 0,
                                            },
                                            rate: { value: 0, unitCode: "" },
                                            minimumRate: {
                                              value: 0,
                                              unitCode: "",
                                            },
                                            maximumRate: {
                                              value: 0,
                                              unitCode: "",
                                            },
                                            incomeAmountBasis: "",
                                            dueDateTime: {
                                              value: "",
                                              timeZoneCode: "",
                                            },
                                            termsOfTradePeriodDuration: 0,
                                            termsOfTradePeriodBasis: "",
                                            payableByDateTime: {
                                              value: "",
                                              timeZoneCode: "",
                                            },
                                            description: "",
                                          },
                                        ];
                                        updateSection(
                                          idx,
                                          "premiums",
                                          newPremiums
                                        );
                                      }}
                                    />
                                  </HStack>

                                  {(premium.instalments || []).map(
                                    (inst, instIdx) => (
                                      <Box
                                        key={inst.id}
                                        borderWidth="1px"
                                        borderRadius="md"
                                        p={4}
                                        mb={4}
                                        borderColor="gray.300">
                                        <UuidPreview uuid={inst.id} />

                                        <SimpleGrid
                                          columns={{ base: 1, md: 4 }}
                                          spacing={4}
                                          mb={4}>
                                          <FormControl>
                                            <FormLabel>Sequence#</FormLabel>
                                            <Input
                                              type="number"
                                              value={inst.sequenceNbr}
                                              onChange={(e) => {
                                                const updated = [
                                                  ...section.premiums,
                                                ];
                                                updated[premiumIdx].instalments[
                                                  instIdx
                                                ].sequenceNbr =
                                                  parseInt(e.target.value) || 0;
                                                updateSection(
                                                  idx,
                                                  "premiums",
                                                  updated
                                                );
                                              }}
                                            />
                                          </FormControl>

                                          <CurrencyAmountInput
                                            label="Amount"
                                            value={inst.amount}
                                            onChange={(value) => {
                                              const updated = [
                                                ...section.premiums,
                                              ];
                                              updated[premiumIdx].instalments[
                                                instIdx
                                              ].amount = value;
                                              updateSection(
                                                idx,
                                                "premiums",
                                                updated
                                              );
                                            }}
                                          />

                                          <RateAmountInput
                                            label="Instalment%"
                                            value={inst.percentage}
                                            onChange={(value) => {
                                              const updated = [
                                                ...section.premiums,
                                              ];
                                              updated[premiumIdx].instalments[
                                                instIdx
                                              ].percentage = value;
                                              updateSection(
                                                idx,
                                                "premiums",
                                                updated
                                              );
                                            }}
                                          />
                                        </SimpleGrid>

                                        <SimpleGrid
                                          columns={{ base: 1, md: 4 }}
                                          spacing={4}
                                          mb={4}>
                                          <RateAmountInput
                                            label="Rate%"
                                            value={inst.rate}
                                            onChange={(value) => {
                                              const updated = [
                                                ...section.premiums,
                                              ];
                                              updated[premiumIdx].instalments[
                                                instIdx
                                              ].rate = value;
                                              updateSection(
                                                idx,
                                                "premiums",
                                                updated
                                              );
                                            }}
                                          />
                                          <RateAmountInput
                                            label="Min Rate%"
                                            value={inst.minimumRate}
                                            onChange={(value) => {
                                              const updated = [
                                                ...section.premiums,
                                              ];
                                              updated[premiumIdx].instalments[
                                                instIdx
                                              ].minimumRate = value;
                                              updateSection(
                                                idx,
                                                "premiums",
                                                updated
                                              );
                                            }}
                                          />
                                          <RateAmountInput
                                            label="MaxRate%"
                                            value={inst.maximumRate}
                                            onChange={(value) => {
                                              const updated = [
                                                ...section.premiums,
                                              ];
                                              updated[premiumIdx].instalments[
                                                instIdx
                                              ].maximumRate = value;
                                              updateSection(
                                                idx,
                                                "premiums",
                                                updated
                                              );
                                            }}
                                          />
                                          <FormControl>
                                            <FormLabel>
                                              Income Amount Basis
                                            </FormLabel>
                                            <CodesetSelect
                                              codesetCode="A90"
                                              value={
                                                inst.incomeAmountBasis
                                                  ? {
                                                      code: inst.incomeAmountBasis,
                                                      description: "",
                                                    }
                                                  : null
                                              }
                                              onChange={(value) => {
                                                const updated = [
                                                  ...section.premiums,
                                                ];
                                                updated[premiumIdx].instalments[
                                                  instIdx
                                                ].incomeAmountBasis =
                                                  value.code;
                                                updateSection(
                                                  idx,
                                                  "premiums",
                                                  updated
                                                );
                                              }}
                                            />
                                          </FormControl>
                                        </SimpleGrid>

                                        <SimpleGrid
                                          columns={{ base: 1, md: 4 }}
                                          spacing={4}
                                          mb={4}>
                                          <FormControl>
                                            <FormLabel>Due Date</FormLabel>
                                            <Input
                                              type="datetime-local"
                                              value={
                                                inst.dueDateTime?.value || ""
                                              }
                                              onChange={(e) => {
                                                const updated = [
                                                  ...section.premiums,
                                                ];
                                                updated[premiumIdx].instalments[
                                                  instIdx
                                                ].dueDateTime = {
                                                  ...inst.dueDateTime,
                                                  value: e.target.value,
                                                };
                                                updateSection(
                                                  idx,
                                                  "premiums",
                                                  updated
                                                );
                                              }}
                                            />
                                          </FormControl>
                                          <FormControl>
                                            <FormLabel>
                                              Terms of Trade Period (Days)
                                            </FormLabel>
                                            <Input
                                              type="number"
                                              value={
                                                inst.termsOfTradePeriodDays ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                const updated = [
                                                  ...section.premiums,
                                                ];
                                                updated[premiumIdx].instalments[
                                                  instIdx
                                                ].termsOfTradePeriodDays =
                                                  parseInt(e.target.value) || 0;
                                                updateSection(
                                                  idx,
                                                  "premiums",
                                                  updated
                                                );
                                              }}
                                            />
                                          </FormControl>
                                          <FormControl>
                                            <FormLabel>
                                              Payable By Date
                                            </FormLabel>
                                            <Input
                                              type="datetime-local"
                                              value={
                                                inst.payableByDateTime?.value ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                const updated = [
                                                  ...section.premiums,
                                                ];
                                                updated[premiumIdx].instalments[
                                                  instIdx
                                                ].payableByDateTime = {
                                                  ...inst.payableByDateTime,
                                                  value: e.target.value,
                                                };
                                                updateSection(
                                                  idx,
                                                  "premiums",
                                                  updated
                                                );
                                              }}
                                            />
                                          </FormControl>
                                        </SimpleGrid>

                                        <FormControl>
                                          <FormLabel>Description</FormLabel>
                                          <Input
                                            value={inst.description}
                                            onChange={(e) => {
                                              const updated = [
                                                ...section.premiums,
                                              ];
                                              updated[premiumIdx].instalments[
                                                instIdx
                                              ].description = e.target.value;
                                              updateSection(
                                                idx,
                                                "premiums",
                                                updated
                                              );
                                            }}
                                          />
                                        </FormControl>

                                        <HStack justify="flex-end">
                                          <IconButton
                                            icon={<HiOutlineTrash />}
                                            size="md"
                                            onClick={() => {
                                              const updated = [
                                                ...section.premiums,
                                              ];
                                              updated[
                                                premiumIdx
                                              ].instalments.splice(instIdx, 1);
                                              updateSection(
                                                idx,
                                                "premiums",
                                                updated
                                              );
                                            }}
                                          />
                                        </HStack>
                                      </Box>
                                    )
                                  )}
                                </Box>
                              </Box>
                            )
                          )}
                        </TabPanel>

                        <TabPanel>
                          <Heading size="md">Premium Adjustment</Heading>

                          <UuidPreview
                            uuid={section.premiumAdjustment?.id || ""}
                          />

                          <SimpleGrid
                            columns={{ base: 1, md: 1 }}
                            spacing={4}
                            mb={4}>
                            <FormControl>
                              <FormLabel>Description</FormLabel>
                              <Input
                                value={
                                  section.premiumAdjustment?.description || ""
                                }
                                onChange={(e) =>
                                  updateSection(idx, "premiumAdjustment", {
                                    ...section.premiumAdjustment,
                                    description: e.target.value,
                                  })
                                }
                              />
                            </FormControl>
                          </SimpleGrid>

                          <SimpleGrid
                            columns={{ base: 1, md: 4 }}
                            spacing={4}
                            mb={4}>
                            <CurrencyAmountInput
                              label="Premium Amount"
                              value={section.premiumAdjustment?.premiumAmount}
                              onChange={(value) =>
                                updateSection(idx, "premiumAdjustment", {
                                  ...section.premiumAdjustment,
                                  premiumAmount: value,
                                })
                              }
                            />

                            <FormControl>
                              <FormLabel>Period Start</FormLabel>
                              <Input
                                value={
                                  section.premiumAdjustment
                                    ?.calculationPeriodStartDateTime?.value ||
                                  ""
                                }
                                onChange={(e) =>
                                  updateSection(idx, "premiumAdjustment", {
                                    ...section.premiumAdjustment,
                                    calculationPeriodStartDateTime: {
                                      ...section.premiumAdjustment
                                        .calculationPeriodStartDateTime,
                                      value: e.target.value,
                                    },
                                  })
                                }
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel>Period End</FormLabel>
                              <Input
                                value={
                                  section.premiumAdjustment
                                    ?.calculationPeriodEndDateTime?.value || ""
                                }
                                onChange={(e) =>
                                  updateSection(idx, "premiumAdjustment", {
                                    ...section.premiumAdjustment,
                                    calculationPeriodEndDateTime: {
                                      ...section.premiumAdjustment
                                        .calculationPeriodEndDateTime,
                                      value: e.target.value,
                                    },
                                  })
                                }
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel>Credit Period (Days)</FormLabel>
                              <Input
                                type="number"
                                value={
                                  section.premiumAdjustment
                                    ?.creditPeriodDurationDays || ""
                                }
                                onChange={(e) =>
                                  updateSection(idx, "premiumAdjustment", {
                                    ...section.premiumAdjustment,
                                    creditPeriodDurationDays:
                                      parseInt(e.target.value) || 0,
                                  })
                                }
                              />
                            </FormControl>
                          </SimpleGrid>

                          <SimpleGrid
                            columns={{ base: 1, md: 4 }}
                            spacing={4}
                            mb={4}>
                            <RateAmountInput
                              label="Rate"
                              value={section.premiumAdjustment?.rate}
                              onChange={(value) =>
                                updateSection(idx, "premiumAdjustment", {
                                  ...section.premiumAdjustment,
                                  rate: value,
                                })
                              }
                            />

                            <RateAmountInput
                              label="Minimum Rate"
                              value={section.premiumAdjustment?.minimumRate}
                              onChange={(value) =>
                                updateSection(idx, "premiumAdjustment", {
                                  ...section.premiumAdjustment,
                                  minimumRate: value,
                                })
                              }
                            />
                            <RateAmountInput
                              label="Maximum Rate"
                              value={section.premiumAdjustment?.maximumRate}
                              onChange={(value) =>
                                updateSection(idx, "premiumAdjustment", {
                                  ...section.premiumAdjustment,
                                  maximumRate: value,
                                })
                              }
                            />
                            <CodesetSelect
                              label="Income Amount Basis"
                              codesetCode="A90"
                              value={
                                section.premiumAdjustment?.basis
                                  ? {
                                      code: section.premiumAdjustment.basis,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(value) =>
                                updateSection(idx, "premiumAdjustment", {
                                  ...section.premiumAdjustment,
                                  basis: value.code,
                                })
                              }
                            />
                          </SimpleGrid>

                          <SimpleGrid
                            columns={{ base: 1, md: 5 }}
                            spacing={4}
                            mb={4}>
                            <CodesetSelect
                              label="Calculation Type"
                              codesetCode="A72"
                              filter="#PremiumAdjustment"
                              value={
                                section.premiumAdjustment?.calculationType
                                  ? {
                                      code: section.premiumAdjustment
                                        .calculationType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(value) =>
                                updateSection(idx, "premiumAdjustment", {
                                  ...section.premiumAdjustment,
                                  calculationType: value.code,
                                })
                              }
                            />

                            <CodesetSelect
                              label="Loading Operation"
                              codesetCode="A105"
                              value={
                                section.premiumAdjustment
                                  ?.calculationLoadingOperationCode
                                  ? {
                                      code: section.premiumAdjustment
                                        .calculationLoadingOperationCode,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(value) =>
                                updateSection(idx, "premiumAdjustment", {
                                  ...section.premiumAdjustment,
                                  calculationLoadingOperationCode: value.code,
                                })
                              }
                            />

                            <CodesetSelect
                              label="Calculation Frequency"
                              codesetCode="A118"
                              value={
                                section.premiumAdjustment
                                  ?.calculationFrequencyCode
                                  ? {
                                      code: section.premiumAdjustment
                                        .calculationFrequencyCode,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(value) =>
                                updateSection(idx, "premiumAdjustment", {
                                  ...section.premiumAdjustment,
                                  calculationFrequencyCode: value.code,
                                })
                              }
                            />

                            <FormControl>
                              <FormLabel>Loading Fraction Numerator</FormLabel>
                              <Input
                                type="number"
                                value={
                                  section.premiumAdjustment
                                    ?.calculationLoadingFractionNumerator || ""
                                }
                                onChange={(e) =>
                                  updateSection(idx, "premiumAdjustment", {
                                    ...section.premiumAdjustment,
                                    calculationLoadingFractionNumerator:
                                      parseInt(e.target.value) || 0,
                                  })
                                }
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel>
                                Loading Fraction Denominator
                              </FormLabel>
                              <Input
                                type="number"
                                value={
                                  section.premiumAdjustment
                                    ?.calculationLoadingFractionDenominator ||
                                  ""
                                }
                                onChange={(e) =>
                                  updateSection(idx, "premiumAdjustment", {
                                    ...section.premiumAdjustment,
                                    calculationLoadingFractionDenominator:
                                      parseInt(e.target.value) || 0,
                                  })
                                }
                              />
                            </FormControl>
                          </SimpleGrid>
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Taxes</Heading>

                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              onClick={() => {
                                const newTaxes = [
                                  ...(section.taxes || []),
                                  {
                                    id: crypto.randomUUID(),
                                    description: "",
                                    applicationType: "",
                                    classCode: "",
                                    type: "",
                                    locations: [],
                                    rateBasis: "",
                                    payableByParty: { type: "", id: "" },
                                    administratorParty: { type: "", id: "" },
                                    baseAmount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                    rate: { value: 0, unitCode: "" },
                                    unitCount: 0,
                                    amount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                  },
                                ];
                                updateSection(idx, "taxes", newTaxes);
                              }}
                            />
                          </HStack>

                          {(section.taxes || []).map((tax, taxIdx) => (
                            <Box
                              key={tax.id}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={6}
                              borderColor="gray.500">
                              <UuidPreview uuid={tax.id} />

                              <SimpleGrid
                                columns={{ base: 1, md: 1 }}
                                spacing={4}
                                mb={4}>
                                <FormControl>
                                  <FormLabel>Description</FormLabel>
                                  <Input
                                    value={tax.description}
                                    onChange={(e) => {
                                      const newTaxes = [...section.taxes];
                                      newTaxes[taxIdx].description =
                                        e.target.value;
                                      updateSection(idx, "taxes", newTaxes);
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 3 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Application"
                                  codesetCode="5025"
                                  value={
                                    tax.applicationType
                                      ? {
                                          code: tax.applicationType,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].applicationType =
                                      value.code;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                                <CodesetSelect
                                  label="Type"
                                  codesetCode="A70"
                                  value={
                                    tax.type
                                      ? { code: tax.type, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].type = value.code;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                                <CodesetSelect
                                  label="Class Code"
                                  codesetCode="B13"
                                  value={
                                    tax.classCode
                                      ? { code: tax.classCode, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].classCode = value.code;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 5 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Rate Basis"
                                  codesetCode="B14"
                                  value={
                                    tax.rateBasis
                                      ? { code: tax.rateBasis, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].rateBasis = value.code;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />

                                <FormControl>
                                  <FormLabel>Unit Count</FormLabel>
                                  <Input
                                    type="number"
                                    value={tax.unitCount}
                                    onChange={(e) => {
                                      const newTaxes = [...section.taxes];
                                      newTaxes[taxIdx].unitCount =
                                        parseInt(e.target.value) || 0;
                                      updateSection(idx, "taxes", newTaxes);
                                    }}
                                  />
                                </FormControl>

                                <CurrencyAmountInput
                                  label="Base Amount"
                                  value={tax.baseAmount}
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].baseAmount = value;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                                <RateAmountInput
                                  label="Rate"
                                  value={tax.rate}
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].rate = value;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />

                                <CurrencyAmountInput
                                  label="Total Amount"
                                  value={tax.amount}
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].amount = value;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                              </SimpleGrid>
                              <SimpleGrid columns={{ base: 1, md: 5 }} mb={2}>
                                <CodesetSelect
                                  label="Payable By"
                                  codesetCode="3035"
                                  value={
                                    tax.payableByParty.type
                                      ? {
                                          code: tax.payableByParty.type,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].payableByParty.type =
                                      value.code;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                                <PartySelectById
                                  label="Party"
                                  role={tax.payableByParty.type}
                                  selectedPartyId={tax.payableByParty.id}
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].payableByParty.id = value;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                                <CodesetSelect
                                  label="Administrator"
                                  codesetCode="3035"
                                  value={
                                    tax.administratorParty.type
                                      ? {
                                          code: tax.administratorParty.type,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].administratorParty.type =
                                      value.code;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                                <PartySelectById
                                  label="Party"
                                  role={tax.administratorParty.type}
                                  selectedPartyId={tax.administratorParty.id}
                                  onChange={(value) => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].administratorParty.id =
                                      value;
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                              </SimpleGrid>

                              <HStack>
                                <Heading size="sm" mb={2}>
                                  Locations
                                </Heading>

                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  size="md"
                                  onClick={() => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes[taxIdx].locations.push({
                                      id: crypto.randomUUID(),
                                      type: "",
                                      agencyCode: "iso",
                                      agencyCodeSetCode: "ISO3166",
                                      code: "",
                                      countryCode: "",
                                      subentityCode: "",
                                      supraentityCode: "",
                                      description: "",
                                      allocationPercentage: {
                                        value: 0,
                                        unitCode: "",
                                      },
                                      ruleCode: "",
                                    });
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                              </HStack>

                              <Box
                                overflowX="auto"
                                mb={4}
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                borderColor="gray.500">
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                  }}>
                                  <thead>
                                    <tr>
                                      <th
                                        style={{
                                          padding: "8px",
                                          borderBottom: "1px solid #ccc",
                                          textAlign: "left",
                                        }}>
                                        Type
                                      </th>
                                      <th
                                        style={{
                                          padding: "8px",
                                          borderBottom: "1px solid #ccc",
                                          textAlign: "left",
                                        }}>
                                        Agency Code
                                      </th>
                                      <th
                                        style={{
                                          padding: "8px",
                                          borderBottom: "1px solid #ccc",
                                          textAlign: "left",
                                        }}>
                                        Agency Code Set
                                      </th>
                                      <th
                                        style={{
                                          padding: "8px",
                                          borderBottom: "1px solid #ccc",
                                          textAlign: "left",
                                        }}>
                                        Code
                                      </th>
                                      <th
                                        style={{
                                          padding: "8px",
                                          borderBottom: "1px solid #ccc",
                                          textAlign: "left",
                                        }}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(tax.locations || []).map(
                                      (loc, locIdx) => (
                                        <tr key={loc.id}>
                                          <td style={{ padding: "8px" }}>
                                            <CodesetSelect
                                              codesetCode="3227"
                                              filter="#Tax"
                                              value={
                                                loc.type
                                                  ? {
                                                      code: loc.type,
                                                      description: "",
                                                    }
                                                  : null
                                              }
                                              onChange={(value) => {
                                                const newTaxes = [
                                                  ...section.taxes,
                                                ];
                                                newTaxes[taxIdx].locations[
                                                  locIdx
                                                ].type = value.code;
                                                updateSection(
                                                  idx,
                                                  "taxes",
                                                  newTaxes
                                                );
                                              }}
                                            />
                                          </td>
                                          <td style={{ padding: "8px" }}>
                                            <CodesetSelect
                                              codesetCode="3055"
                                              value={
                                                loc.agencyCode
                                                  ? {
                                                      code: loc.agencyCode,
                                                      description: "",
                                                    }
                                                  : null
                                              }
                                              onChange={(value) => {
                                                const newTaxes = [
                                                  ...section.taxes,
                                                ];
                                                newTaxes[taxIdx].locations[
                                                  locIdx
                                                ].agencyCode = value.code;
                                                updateSection(
                                                  idx,
                                                  "taxes",
                                                  newTaxes
                                                );
                                              }}
                                            />
                                          </td>
                                          <td style={{ padding: "8px" }}>
                                            <CodesetSelect
                                              codesetCode="C01"
                                              filter="#Tax"
                                              value={
                                                loc.agencyCodeSetCode
                                                  ? {
                                                      code: loc.agencyCodeSetCode,
                                                      description: "",
                                                    }
                                                  : null
                                              }
                                              onChange={(value) => {
                                                const newTaxes = [
                                                  ...section.taxes,
                                                ];
                                                const location =
                                                  newTaxes[taxIdx].locations[
                                                    locIdx
                                                  ];
                                                location.agencyCodeSetCode =
                                                  value.code;
                                                location.agencyCode =
                                                  value.codeListAgency; // ✅ update agencyCode as well
                                                updateSection(
                                                  idx,
                                                  "taxes",
                                                  newTaxes
                                                );
                                              }}
                                            />
                                          </td>
                                          <td style={{ padding: "8px" }}>
                                            <CodesetSelect
                                              codesetCode={
                                                loc.agencyCodeSetCode
                                              }
                                              value={
                                                loc.code
                                                  ? {
                                                      code: loc.code,
                                                      description: "",
                                                    }
                                                  : null
                                              }
                                              onChange={(value) => {
                                                const newTaxes = [
                                                  ...section.taxes,
                                                ];
                                                newTaxes[taxIdx].locations[
                                                  locIdx
                                                ].code = value.code;
                                                updateSection(
                                                  idx,
                                                  "taxes",
                                                  newTaxes
                                                );
                                              }}
                                            />
                                          </td>
                                          <td
                                            style={{
                                              padding: "8px",
                                              textAlign: "center",
                                            }}>
                                            <IconButton
                                              icon={<HiOutlineTrash />}
                                              aria-label="Delete location"
                                              size="sm"
                                              onClick={() => {
                                                const newTaxes = [
                                                  ...section.taxes,
                                                ];
                                                newTaxes[
                                                  taxIdx
                                                ].locations.splice(locIdx, 1);
                                                updateSection(
                                                  idx,
                                                  "taxes",
                                                  newTaxes
                                                );
                                              }}
                                            />
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </Box>
                              <HStack justify="flex-end">
                                <IconButton
                                  icon={<HiOutlineTrash />}
                                  size="md"
                                  onClick={() => {
                                    const newTaxes = [...section.taxes];
                                    newTaxes.splice(taxIdx, 1);
                                    updateSection(idx, "taxes", newTaxes);
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Commissions</Heading>

                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              onClick={() => {
                                const newCommissions = [
                                  ...(section.commissions || []),
                                  {
                                    id: crypto.randomUUID(),
                                    type: "",
                                    description: "",
                                    classCode: "",
                                    basis: "",
                                    interpolationBasis: "",
                                    lossRatioBasis: "",
                                    carryForwardCode: "",
                                    carryForwardIntervalPeriodType: "",
                                    carryForwardInterval: 0,
                                    calculationType: "",
                                    calculationFrequencyCode: "",
                                    firstCalculationDateTime: {
                                      value: "",
                                      timeZoneCode: "",
                                    },
                                    calculationStartDateTime: {
                                      value: "",
                                      timeZoneCode: "",
                                    },
                                    calculationEndDateTime: {
                                      value: "",
                                      timeZoneCode: "",
                                    },
                                    managementExpensesPercentage: {
                                      value: 0,
                                      unitCode: "",
                                    },
                                    payableToParty: { type: "", id: "" },
                                    payableByParty: { type: "", id: "" },
                                    commissionValues: [],
                                  },
                                ];
                                updateSection(
                                  idx,
                                  "commissions",
                                  newCommissions
                                );
                              }}
                            />
                          </HStack>

                          {(section.commissions || []).map((comm, commIdx) => (
                            <Box
                              key={comm.id}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={6}
                              borderColor="gray.500">
                              <UuidPreview uuid={comm.id} />

                              <SimpleGrid
                                columns={{ base: 1, md: 1 }}
                                spacing={4}
                                mb={4}>
                                <FormControl>
                                  <FormLabel>Description</FormLabel>
                                  <Input
                                    value={comm.description}
                                    onChange={(e) => {
                                      const newComms = [...section.commissions];
                                      newComms[commIdx].description =
                                        e.target.value;
                                      updateSection(
                                        idx,
                                        "commissions",
                                        newComms
                                      );
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 5 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Type"
                                  codesetCode="B46"
                                  value={
                                    comm.type
                                      ? { code: comm.type, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].type = value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <CodesetSelect
                                  label="Class"
                                  codesetCode="B53"
                                  filter="#Commission"
                                  value={
                                    comm.classCode
                                      ? {
                                          code: comm.classCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].classCode = value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <CodesetSelect
                                  label="Basis"
                                  codesetCode="A67"
                                  value={
                                    comm.basis
                                      ? { code: comm.basis, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].basis = value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <CodesetSelect
                                  label="Interpolation Basis"
                                  codesetCode="B55"
                                  value={
                                    comm.interpolationBasis
                                      ? {
                                          code: comm.interpolationBasis,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].interpolationBasis =
                                      value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <CodesetSelect
                                  label="Loss Ratio Basis"
                                  codesetCode="B56"
                                  value={
                                    comm.lossRatioBasis
                                      ? {
                                          code: comm.lossRatioBasis,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].lossRatioBasis =
                                      value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 3 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Carry Forward Code"
                                  codesetCode="A77"
                                  filter="#Commission"
                                  value={
                                    comm.carryForwardCode
                                      ? {
                                          code: comm.carryForwardCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].carryForwardCode =
                                      value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <CodesetSelect
                                  label="Carry Forward Period Type"
                                  codesetCode="2151"
                                  filter="#Commission"
                                  value={
                                    comm.carryForwardIntervalPeriodType
                                      ? {
                                          code: comm.carryForwardIntervalPeriodType,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[
                                      commIdx
                                    ].carryForwardIntervalPeriodType =
                                      value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <FormControl>
                                  <FormLabel>Carry Forward Interval</FormLabel>
                                  <Input
                                    type="number"
                                    value={comm.carryForwardInterval || ""}
                                    onChange={(e) => {
                                      const newComms = [...section.commissions];
                                      newComms[commIdx].carryForwardInterval =
                                        parseInt(e.target.value) || 0;
                                      updateSection(
                                        idx,
                                        "commissions",
                                        newComms
                                      );
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 2 }}
                                spacing={4}
                                mb={4}>
                                <RateAmountInput
                                  label="Management Expenses %"
                                  value={comm.managementExpensesPercentage}
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[
                                      commIdx
                                    ].managementExpensesPercentage = value;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 5 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Calculation Type"
                                  codesetCode="A72"
                                  filter="#Commission"
                                  value={
                                    comm.calculationType
                                      ? {
                                          code: comm.calculationType,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].calculationType =
                                      value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />

                                <CodesetSelect
                                  label="Calculation Frequency"
                                  codesetCode="A118"
                                  value={
                                    comm.calculationFrequencyCode
                                      ? {
                                          code: comm.calculationFrequencyCode,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].calculationFrequencyCode =
                                      value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <FormControl>
                                  <FormLabel>First Calculation Date</FormLabel>
                                  <Input
                                    type="datetime-local"
                                    value={
                                      comm.firstCalculationDateTime?.value || ""
                                    }
                                    onChange={(e) => {
                                      const newComms = [...section.commissions];
                                      newComms[
                                        commIdx
                                      ].firstCalculationDateTime = {
                                        ...comm.firstCalculationDateTime,
                                        value: e.target.value,
                                      };
                                      updateSection(
                                        idx,
                                        "commissions",
                                        newComms
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel>Calculation Start Date</FormLabel>
                                  <Input
                                    type="datetime-local"
                                    value={
                                      comm.calculationStartDateTime?.value || ""
                                    }
                                    onChange={(e) => {
                                      const newComms = [...section.commissions];
                                      newComms[
                                        commIdx
                                      ].calculationStartDateTime = {
                                        ...comm.calculationStartDateTime,
                                        value: e.target.value,
                                      };
                                      updateSection(
                                        idx,
                                        "commissions",
                                        newComms
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel>Calculation End Date</FormLabel>
                                  <Input
                                    type="datetime-local"
                                    value={
                                      comm.calculationEndDateTime?.value || ""
                                    }
                                    onChange={(e) => {
                                      const newComms = [...section.commissions];
                                      newComms[commIdx].calculationEndDateTime =
                                        {
                                          ...comm.calculationEndDateTime,
                                          value: e.target.value,
                                        };
                                      updateSection(
                                        idx,
                                        "commissions",
                                        newComms
                                      );
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid columns={{ base: 1, md: 5 }} mb={4}>
                                <CodesetSelect
                                  label="Payable By"
                                  codesetCode="3035"
                                  value={
                                    comm.payableByParty.type
                                      ? {
                                          code: comm.payableByParty.type,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].payableByParty.type =
                                      value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <PartySelectById
                                  label="Party"
                                  role={comm.payableByParty.type}
                                  selectedPartyId={comm.payableByParty.id}
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].payableByParty.id = value;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <CodesetSelect
                                  label="Payable To"
                                  codesetCode="3035"
                                  value={
                                    comm.payableToParty.type
                                      ? {
                                          code: comm.payableToParty.type,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].payableToParty.type =
                                      value.code;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                                <PartySelectById
                                  label="Party"
                                  role={comm.payableToParty.type}
                                  selectedPartyId={comm.payableToParty.id}
                                  onChange={(value) => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].payableToParty.id = value;
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                              </SimpleGrid>

                              <HStack>
                                <Heading size="sm" mb={2}>
                                  Commission Values
                                </Heading>

                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  size="md"
                                  onClick={() => {
                                    const newComms = [...section.commissions];
                                    newComms[commIdx].commissionValues.push({
                                      basis: "",
                                      sequenceNbr: 0,
                                      percentage: { value: 0, unitCode: "" },
                                      lossRatioPercentage: {
                                        value: 0,
                                        unitCode: "",
                                      },
                                      cedingCommissionPercentage: {
                                        value: 0,
                                        unitCode: "",
                                      },
                                      amount: {
                                        shareCode: "",
                                        ccyIndicator: "",
                                        ccy: "",
                                        value: 0,
                                      },
                                    });
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                              </HStack>

                              <Box
                                overflowX="auto"
                                mb={4}
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                borderColor="gray.500">
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th textAlign="left">Basis</Th>
                                      <Th textAlign="left">Percentage</Th>
                                      <Th textAlign="left">Loss Ratio %</Th>
                                      <Th textAlign="left">
                                        Ceding Commission %
                                      </Th>
                                      <Th textAlign="left">Amount</Th>
                                      <Th />
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {(comm.commissionValues || []).map(
                                      (cv, cvIdx) => (
                                        <Tr key={cvIdx}>
                                          <Td>
                                            <CodesetSelect
                                              codesetCode="B54"
                                              value={
                                                cv.basis
                                                  ? {
                                                      code: cv.basis,
                                                      description: "",
                                                    }
                                                  : null
                                              }
                                              onChange={(value) => {
                                                const newComms = [
                                                  ...section.commissions,
                                                ];
                                                newComms[
                                                  commIdx
                                                ].commissionValues[
                                                  cvIdx
                                                ].basis = value.code;
                                                updateSection(
                                                  idx,
                                                  "commissions",
                                                  newComms
                                                );
                                              }}
                                            />
                                          </Td>
                                          <Td>
                                            <RateAmountInput
                                              value={cv.percentage}
                                              onChange={(value) => {
                                                const newComms = [
                                                  ...section.commissions,
                                                ];
                                                newComms[
                                                  commIdx
                                                ].commissionValues[
                                                  cvIdx
                                                ].percentage = value;
                                                updateSection(
                                                  idx,
                                                  "commissions",
                                                  newComms
                                                );
                                              }}
                                            />
                                          </Td>
                                          <Td>
                                            <RateAmountInput
                                              value={cv.lossRatioPercentage}
                                              onChange={(value) => {
                                                const newComms = [
                                                  ...section.commissions,
                                                ];
                                                newComms[
                                                  commIdx
                                                ].commissionValues[
                                                  cvIdx
                                                ].lossRatioPercentage = value;
                                                updateSection(
                                                  idx,
                                                  "commissions",
                                                  newComms
                                                );
                                              }}
                                            />
                                          </Td>
                                          <Td>
                                            <RateAmountInput
                                              value={
                                                cv.cedingCommissionPercentage
                                              }
                                              onChange={(value) => {
                                                const newComms = [
                                                  ...section.commissions,
                                                ];
                                                newComms[
                                                  commIdx
                                                ].commissionValues[
                                                  cvIdx
                                                ].cedingCommissionPercentage =
                                                  value;
                                                updateSection(
                                                  idx,
                                                  "commissions",
                                                  newComms
                                                );
                                              }}
                                            />
                                          </Td>
                                          <Td>
                                            <CurrencyAmountInput
                                              value={cv.amount}
                                              onChange={(value) => {
                                                const newComms = [
                                                  ...section.commissions,
                                                ];
                                                newComms[
                                                  commIdx
                                                ].commissionValues[
                                                  cvIdx
                                                ].amount = value;
                                                updateSection(
                                                  idx,
                                                  "commissions",
                                                  newComms
                                                );
                                              }}
                                            />
                                          </Td>
                                          <Td>
                                            <IconButton
                                              icon={<HiOutlineTrash />}
                                              aria-label="Delete Commission Value"
                                              size="sm"
                                              onClick={() => {
                                                const newComms = [
                                                  ...section.commissions,
                                                ];
                                                newComms[
                                                  commIdx
                                                ].commissionValues.splice(
                                                  cvIdx,
                                                  1
                                                );
                                                updateSection(
                                                  idx,
                                                  "commissions",
                                                  newComms
                                                );
                                              }}
                                            />
                                          </Td>
                                        </Tr>
                                      )
                                    )}
                                  </Tbody>
                                </Table>
                              </Box>

                              <HStack justify="flex-end">
                                <IconButton
                                  icon={<HiOutlineTrash />}
                                  size="md"
                                  onClick={() => {
                                    const newComms = [...section.commissions];
                                    newComms.splice(commIdx, 1);
                                    updateSection(idx, "commissions", newComms);
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Other Deductions</Heading>

                            <IconButton
                              size="sm"
                              mb={4}
                              icon={<HiOutlinePlus />}
                              aria-label="Add Deduction"
                              onClick={() => {
                                const newDeductions = [
                                  ...(section.otherDeductions || []),
                                  {
                                    id: crypto.randomUUID(),
                                    type: "",
                                    description: "",
                                    classCode: "",
                                    percentage: { value: 0, unitCode: "" },
                                    basis: "",
                                    amount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                    payableToParty: { type: "", id: "" },
                                    payableByParty: { type: "", id: "" },
                                    serviceProvidedToParty: {
                                      type: "",
                                      id: "",
                                    },
                                  },
                                ];
                                updateSection(
                                  idx,
                                  "otherDeductions",
                                  newDeductions
                                );
                              }}
                            />
                          </HStack>
                          '
                          {(section.otherDeductions || []).map(
                            (ded, dedIdx) => (
                              <Box
                                key={ded.id}
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                mb={4}
                                borderColor="gray.500">
                                <UuidPreview uuid={ded.id} />

                                <SimpleGrid
                                  columns={{ base: 1, md: 1 }}
                                  spacing={4}
                                  mb={4}>
                                  <FormControl>
                                    <FormLabel>Description</FormLabel>
                                    <Input
                                      value={ded.description}
                                      onChange={(e) => {
                                        const updated = [
                                          ...section.otherDeductions,
                                        ];
                                        updated[dedIdx].description =
                                          e.target.value;
                                        updateSection(
                                          idx,
                                          "otherDeductions",
                                          updated
                                        );
                                      }}
                                    />
                                  </FormControl>
                                </SimpleGrid>

                                <SimpleGrid
                                  columns={{ base: 1, md: 3 }}
                                  spacing={4}
                                  mb={4}>
                                  <CodesetSelect
                                    label="Type"
                                    codesetCode="A89"
                                    filter="#OtherDeduction"
                                    value={
                                      ded.type
                                        ? { code: ded.type, description: "" }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].type = value.code;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                  <CodesetSelect
                                    label="Class Code"
                                    codesetCode="B53"
                                    filter="#OtherDeduction"
                                    value={
                                      ded.classCode
                                        ? {
                                            code: ded.classCode,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].classCode = value.code;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                </SimpleGrid>

                                <SimpleGrid
                                  columns={{ base: 1, md: 3 }}
                                  spacing={4}
                                  mb={4}>
                                  <CodesetSelect
                                    label="Basis"
                                    codesetCode="A67"
                                    value={
                                      ded.basis
                                        ? { code: ded.basis, description: "" }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].basis = value.code;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />

                                  <RateAmountInput
                                    label="Percentage"
                                    value={ded.percentage}
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].percentage = value;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                  <CurrencyAmountInput
                                    label="Amount"
                                    value={ded.amount}
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].amount = value;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                </SimpleGrid>

                                <SimpleGrid columns={{ base: 1, md: 6 }} mb={4}>
                                  <CodesetSelect
                                    label="Payable By"
                                    codesetCode="3035"
                                    value={
                                      ded.payableByParty.type
                                        ? {
                                            code: ded.payableByParty.type,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].payableByParty.type =
                                        value.code;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                  <PartySelectById
                                    label="Party"
                                    role={ded.payableByParty.type}
                                    selectedPartyId={ded.payableByParty.id}
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].payableByParty.id = value;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                  <CodesetSelect
                                    label="Payable To"
                                    codesetCode="3035"
                                    value={
                                      ded.payableToParty.type
                                        ? {
                                            code: ded.payableToParty.type,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].payableToParty.type =
                                        value.code;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                  <PartySelectById
                                    label="Party"
                                    role={ded.payableToParty.type}
                                    selectedPartyId={ded.payableToParty.id}
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[dedIdx].payableToParty.id = value;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                  <CodesetSelect
                                    label="Service Provided To"
                                    codesetCode="3035"
                                    value={
                                      ded.serviceProvidedToParty.type
                                        ? {
                                            code: ded.serviceProvidedToParty
                                              .type,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[
                                        dedIdx
                                      ].serviceProvidedToParty.type =
                                        value.code;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                  <PartySelectById
                                    label="Party"
                                    role={ded.serviceProvidedToParty.type}
                                    selectedPartyId={
                                      ded.serviceProvidedToParty.id
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated[
                                        dedIdx
                                      ].serviceProvidedToParty.id = value;
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                </SimpleGrid>

                                <HStack justify="flex-end">
                                  <IconButton
                                    size="sm"
                                    icon={<HiOutlineTrash />}
                                    aria-label="Delete Deduction"
                                    onClick={() => {
                                      const updated = [
                                        ...section.otherDeductions,
                                      ];
                                      updated.splice(dedIdx, 1);
                                      updateSection(
                                        idx,
                                        "otherDeductions",
                                        updated
                                      );
                                    }}
                                  />
                                </HStack>
                              </Box>
                            )
                          )}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Brokerage</Heading>

                            <IconButton
                              size="sm"
                              mb={4}
                              icon={<HiOutlinePlus />}
                              aria-label="Add Brokerage"
                              onClick={() => {
                                const newBrokerages = [
                                  ...(section.brokerages || []),
                                  {
                                    id: crypto.randomUUID(),
                                    type: "",
                                    description: "",
                                    classCode: "",
                                    percentage: { value: 0, unitCode: "" },
                                    basis: "",
                                    amount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                    baseAmount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                    payableToParty: { type: "", id: "" },
                                    payableByParty: { type: "", id: "" },
                                  },
                                ];
                                updateSection(idx, "brokerages", newBrokerages);
                              }}
                            />
                          </HStack>

                          {(section.brokerages || []).map((brk, brkIdx) => (
                            <Box
                              key={brk.id}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={4}
                              borderColor="gray.500">
                              <UuidPreview uuid={brk.id} />

                              <SimpleGrid
                                columns={{ base: 1, md: 1 }}
                                spacing={4}
                                mb={4}>
                                <FormControl>
                                  <FormLabel>Description</FormLabel>
                                  <Input
                                    value={brk.description}
                                    onChange={(e) => {
                                      const updated = [...section.brokerages];
                                      updated[brkIdx].description =
                                        e.target.value;
                                      updateSection(idx, "brokerages", updated);
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 3 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Type"
                                  codesetCode="B63"
                                  value={
                                    brk.type
                                      ? { code: brk.type, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].type = value.code;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />

                                <CodesetSelect
                                  label="Class Code"
                                  codesetCode="B53"
                                  filter="#Brokerage"
                                  value={
                                    brk.classCode
                                      ? { code: brk.classCode, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].classCode = value.code;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 4 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Basis"
                                  codesetCode="A67"
                                  value={
                                    brk.basis
                                      ? { code: brk.basis, description: "" }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].basis = value.code;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />

                                <RateAmountInput
                                  label="Percentage"
                                  value={brk.percentage}
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].percentage = value;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                                <CurrencyAmountInput
                                  label="Amount"
                                  value={brk.amount}
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].amount = value;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                                <CurrencyAmountInput
                                  label="Base Amount"
                                  value={brk.baseAmount}
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].baseAmount = value;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 4 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Payable By"
                                  codesetCode="3035"
                                  value={
                                    brk.payableByParty.type
                                      ? {
                                          code: brk.payableByParty.type,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].payableByParty.type =
                                      value.code;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                                <PartySelectById
                                  label="Party"
                                  role={brk.payableByParty.type}
                                  selectedPartyId={brk.payableByParty.id}
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].payableByParty.id = value;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                                <CodesetSelect
                                  label="Payable To"
                                  codesetCode="3035"
                                  value={
                                    brk.payableToParty.type
                                      ? {
                                          code: brk.payableToParty.type,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].payableToParty.type =
                                      value.code;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                                <PartySelectById
                                  label="Party"
                                  role={brk.payableToParty.type}
                                  selectedPartyId={brk.payableToParty.id}
                                  onChange={(value) => {
                                    const updated = [...section.brokerages];
                                    updated[brkIdx].payableToParty.id = value;
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                              </SimpleGrid>

                              <HStack justify="flex-end">
                                <IconButton
                                  size="sm"
                                  icon={<HiOutlineTrash />}
                                  aria-label="Delete Brokerage"
                                  onClick={() => {
                                    const updated = [...section.brokerages];
                                    updated.splice(brkIdx, 1);
                                    updateSection(idx, "brokerages", updated);
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Reinstatements</Heading>

                            <IconButton
                              size="sm"
                              mb={4}
                              icon={<HiOutlinePlus />}
                              aria-label="Add Reinstatement"
                              onClick={() => {
                                const newReinstatements = [
                                  ...(section.reinstatements || []),
                                  {
                                    id: crypto.randomUUID(),
                                    sequenceNbr: 0,
                                    description: "",
                                    unlimitedReinstatementsIndicator: "",
                                    numberOfReinstatements: 0,
                                    brokeragePercentage: {
                                      value: 0,
                                      unitCode: "",
                                    },
                                    brokerageAmount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                    calculationType: "",
                                    premiumRate: { value: 0, unitCode: "" },
                                    premiumAmount: {
                                      shareCode: "",
                                      ccyIndicator: "",
                                      ccy: "",
                                      value: 0,
                                    },
                                  },
                                ];
                                updateSection(
                                  idx,
                                  "reinstatements",
                                  newReinstatements
                                );
                              }}
                            />
                          </HStack>

                          {(section.reinstatements || []).map((rst, rstIdx) => (
                            <Box
                              key={rst.id}
                              borderWidth="1px"
                              borderRadius="md"
                              p={4}
                              mb={4}
                              borderColor="gray.500">
                              <UuidPreview uuid={rst.id} />

                              <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                  value={rst.description}
                                  onChange={(e) => {
                                    const updated = [...section.reinstatements];
                                    updated[rstIdx].description =
                                      e.target.value;
                                    updateSection(
                                      idx,
                                      "reinstatements",
                                      updated
                                    );
                                  }}
                                />
                              </FormControl>

                              <SimpleGrid columns={{ base: 1, md: 4 }} mb={4}>
                                <FormControl width={150}>
                                  <FormLabel>Sequence #</FormLabel>
                                  <Input
                                    type="number"
                                    value={rst.sequenceNbr}
                                    onChange={(e) => {
                                      const updated = [
                                        ...section.reinstatements,
                                      ];
                                      updated[rstIdx].sequenceNbr =
                                        parseInt(e.target.value) || 0;
                                      updateSection(
                                        idx,
                                        "reinstatements",
                                        updated
                                      );
                                    }}
                                  />
                                </FormControl>

                                <CodesetSelect
                                  label="Unlimited?"
                                  codesetCode="JVRI018"
                                  value={
                                    rst.unlimitedReinstatementsIndicator
                                      ? {
                                          code: rst.unlimitedReinstatementsIndicator,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const updated = [...section.reinstatements];
                                    updated[
                                      rstIdx
                                    ].unlimitedReinstatementsIndicator =
                                      value.code;
                                    updateSection(
                                      idx,
                                      "reinstatements",
                                      updated
                                    );
                                  }}
                                />

                                <FormControl width={150}>
                                  <FormLabel width={150}>
                                    # Reinstatements
                                  </FormLabel>
                                  <Input
                                    type="number"
                                    value={rst.numberOfReinstatements}
                                    onChange={(e) => {
                                      const updated = [
                                        ...section.reinstatements,
                                      ];
                                      updated[rstIdx].numberOfReinstatements =
                                        parseInt(e.target.value) || 0;
                                      updateSection(
                                        idx,
                                        "reinstatements",
                                        updated
                                      );
                                    }}
                                  />
                                </FormControl>
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 3 }}
                                spacing={4}
                                mb={4}>
                                <CodesetSelect
                                  label="Calculation Type"
                                  codesetCode="A72"
                                  filter="#ContractReinstatement"
                                  value={
                                    rst.calculationType
                                      ? {
                                          code: rst.calculationType,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const updated = [...section.reinstatements];
                                    updated[rstIdx].calculationType =
                                      value.code;
                                    updateSection(
                                      idx,
                                      "reinstatements",
                                      updated
                                    );
                                  }}
                                />
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 3 }}
                                spacing={4}
                                mb={4}>
                                <CurrencyAmountInput
                                  label="Premium Amount"
                                  value={rst.premiumAmount}
                                  onChange={(value) => {
                                    const updated = [...section.reinstatements];
                                    updated[rstIdx].premiumAmount = value;
                                    updateSection(
                                      idx,
                                      "reinstatements",
                                      updated
                                    );
                                  }}
                                />

                                <RateAmountInput
                                  label="Premium Rate"
                                  value={rst.premiumRate}
                                  onChange={(value) => {
                                    const updated = [...section.reinstatements];
                                    updated[rstIdx].premiumRate = value;
                                    updateSection(
                                      idx,
                                      "reinstatements",
                                      updated
                                    );
                                  }}
                                />
                              </SimpleGrid>

                              <SimpleGrid
                                columns={{ base: 1, md: 3 }}
                                spacing={4}
                                mb={4}>
                                <CurrencyAmountInput
                                  label="Brokerage Amount"
                                  value={rst.brokerageAmount}
                                  onChange={(value) => {
                                    const updated = [...section.reinstatements];
                                    updated[rstIdx].brokerageAmount = value;
                                    updateSection(
                                      idx,
                                      "reinstatements",
                                      updated
                                    );
                                  }}
                                />
                                <RateAmountInput
                                  label="Brokerage %"
                                  value={rst.brokeragePercentage}
                                  onChange={(value) => {
                                    const updated = [...section.reinstatements];
                                    updated[rstIdx].brokeragePercentage = value;
                                    updateSection(
                                      idx,
                                      "reinstatements",
                                      updated
                                    );
                                  }}
                                />
                              </SimpleGrid>

                              <HStack justify="flex-end">
                                <IconButton
                                  size="sm"
                                  icon={<HiOutlineTrash />}
                                  aria-label="Delete Reinstatement"
                                  onClick={() => {
                                    const updated = [...section.reinstatements];
                                    updated.splice(rstIdx, 1);
                                    updateSection(
                                      idx,
                                      "reinstatements",
                                      updated
                                    );
                                  }}
                                />
                              </HStack>
                            </Box>
                          ))}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Contract Parties</Heading>

                            <IconButton
                              size="sm"
                              mb={4}
                              icon={<HiOutlinePlus />}
                              aria-label="Add Contract Party"
                              onClick={() => {
                                const newContractParties = [
                                  ...(section.contractParties || []),
                                  {
                                    id: crypto.randomUUID(),
                                    type: "",
                                    roleCode: "",
                                    participantFunctionCode: "",
                                    party: {
                                      _id: crypto.randomUUID(),
                                      roleCode: "",
                                      partyIdentities: [
                                        {
                                          id: "",
                                          agencyCode: "",
                                        },
                                      ],
                                      name: "",
                                      address: {
                                        type: "",
                                        numberAndStreet: "",
                                        subentityCode: "",
                                        cityName: "",
                                        postalCode: "",
                                        countryCode: "",
                                      },
                                      contact: {
                                        description: "",
                                        roleCode: "",
                                        personName: "",
                                        telephone: "",
                                        fax: "",
                                        email: "",
                                      },
                                    },
                                    references: [
                                      {
                                        ownerCode: "",
                                        type: "",
                                        value: "",
                                      },
                                    ],
                                    classifications: [
                                      {
                                        type: "",
                                        agencyCode: "",
                                        agencyCodeSetCode: "",
                                        description: "",
                                        code: "",
                                        ruleCode: "",
                                      },
                                    ],
                                  },
                                ];
                                updateSection(
                                  idx,
                                  "contractParties",
                                  newContractParties
                                );
                              }}
                            />
                          </HStack>

                          {(section.contractParties || []).map(
                            (contractParty, partyIdx) => (
                              <Box
                                key={contractParty.id}
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                mb={4}
                                borderColor="gray.500">
                                <UuidPreview uuid={contractParty.id} />

                                <SimpleGrid columns={{ base: 1, md: 4 }} mb={4}>
                                  <CodesetSelect
                                    label="Type"
                                    codesetCode="B31"
                                    value={
                                      contractParty.type
                                        ? {
                                            code: contractParty.type,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractParties,
                                      ];
                                      updated[partyIdx].type = value.code;
                                      updateSection(
                                        idx,
                                        "contractParties",
                                        updated
                                      );
                                    }}
                                  />
                                  <CodesetSelect
                                    label="Role"
                                    codesetCode="3035"
                                    filter="#ContractPartyRole"
                                    value={
                                      contractParty.roleCode
                                        ? {
                                            code: contractParty.roleCode,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractParties,
                                      ];
                                      updated[partyIdx].roleCode = value.code;
                                      updateSection(
                                        idx,
                                        "contractParties",
                                        updated
                                      );
                                    }}
                                  />
                                  <CodesetSelect
                                    label="Participant Function"
                                    codesetCode="B06"
                                    value={
                                      contractParty.participantFunctionCode
                                        ? {
                                            code: contractParty.participantFunctionCode,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractParties,
                                      ];
                                      updated[
                                        partyIdx
                                      ].participantFunctionCode = value.code;
                                      updateSection(
                                        idx,
                                        "contractParties",
                                        updated
                                      );
                                    }}
                                  />
                                </SimpleGrid>
                                <PartySelect
                                  label="Party"
                                  role={
                                    section.contractParties[partyIdx].roleCode
                                  }
                                  selectedParty={
                                    section.contractParties[partyIdx].party
                                  }
                                  onChange={(party) => {
                                    const updated = [
                                      ...section.contractParties,
                                    ];
                                    updated[partyIdx] = {
                                      ...updated[partyIdx],
                                      party: { ...party },
                                    };
                                    updateSection(
                                      idx,
                                      "contractParties",
                                      updated
                                    );
                                  }}
                                />
                                <Box mb={2}>
                                  <ReferencesEditor
                                    filter="#PartyClassification"
                                    defaultOwnerCode={
                                      section.contractParties[partyIdx].roleCode
                                    }
                                    references={
                                      section.contractParties[partyIdx]
                                        .references || []
                                    }
                                    onChange={(references) => {
                                      const updated = [
                                        ...section.contractParties,
                                      ];
                                      updated[partyIdx].references = references;
                                      updateSection(
                                        idx,
                                        "contractParties",
                                        updated
                                      );
                                    }}
                                  />
                                </Box>
                                <Box>
                                  <ClassificationsEditor
                                    filter="#PartyClassification"
                                    classifications={
                                      section.contractParties[partyIdx]
                                        .classifications || []
                                    }
                                    onChange={(classifications) => {
                                      const updated = [
                                        ...section.contractParties,
                                      ];
                                      updated[partyIdx].classifications =
                                        classifications;
                                      updateSection(
                                        idx,
                                        "contractParties",
                                        updated
                                      );
                                    }}
                                  />
                                </Box>

                                <HStack justify="flex-end">
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlineTrash />}
                                    aria-label="Delete Contract Party"
                                    onClick={() => {
                                      const updated = [
                                        ...section.contractParties,
                                      ];
                                      updated.splice(partyIdx, 1);
                                      updateSection(
                                        idx,
                                        "contractParties",
                                        updated
                                      );
                                    }}
                                  />
                                </HStack>
                              </Box>
                            )
                          )}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Contract Market</Heading>

                            <IconButton
                              size="sm"
                              mb={4}
                              icon={<HiOutlinePlus />}
                              aria-label="Add Carrier"
                              onClick={() => {
                                const newContractMarkets = [
                                  ...(section.contractMarkets || []),
                                  {
                                    id: crypto.randomUUID(),
                                    party: {
                                      _id: crypto.randomUUID(),
                                      roleCode: "",
                                      partyIdentities: [
                                        {
                                          id: "",
                                          agencyCode: "",
                                        },
                                      ],
                                      name: "",
                                      address: {
                                        type: "",
                                        numberAndStreet: "",
                                        subentityCode: "",
                                        cityName: "",
                                        postalCode: "",
                                        countryCode: "",
                                      },
                                      contact: {
                                        description: "",
                                        roleCode: "",
                                        personName: "",
                                        telephone: "",
                                        fax: "",
                                        email: "",
                                      },
                                    },
                                    participationBasis: "",
                                    references: [
                                      {
                                        ownerCode: "",
                                        type: "",
                                        value: "",
                                      },
                                    ],
                                    classifications: [
                                      {
                                        type: "",
                                        agencyCode: "",
                                        agencyCodeSetCode: "",
                                        description: "",
                                        code: "",
                                        ruleCode: "",
                                      },
                                    ],
                                  },
                                ];
                                updateSection(
                                  idx,
                                  "contractMarkets",
                                  newContractMarkets
                                );
                              }}
                            />
                          </HStack>

                          {(section.contractMarkets || []).map(
                            (contractMarket, marketIdx) => (
                              <Box
                                key={contractMarket.id}
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                mb={4}
                                borderColor="gray.500">
                                <UuidPreview uuid={contractMarket.id} />

                                <SimpleGrid columns={{ base: 1, md: 5 }} mb={2}>
                                  <PartySelect
                                    label="Carrier"
                                    role={
                                      contractMarket.party?.roleCode ||
                                      contractMarket.party?.type ||
                                      "Insurer"
                                    }
                                    selectedParty={contractMarket.party}
                                    onChange={(clickedparty) => {
                                      // Always create a new array and new objects to avoid mutating state
                                      const updatedMarkets =
                                        section.contractMarkets.map((cm, i) =>
                                          i === marketIdx
                                            ? {
                                                ...cm,
                                                party: {
                                                  ...clickedparty,
                                                },
                                              }
                                            : cm
                                        );
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updatedMarkets
                                      );
                                    }}
                                  />

                                  <CodesetSelect
                                    label="Participant Function"
                                    codesetCode="B06"
                                    filter="#Carrier"
                                    value={
                                      contractMarket.participantFunctionCode
                                        ? {
                                            code: contractMarket.participantFunctionCode,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated[
                                        marketIdx
                                      ].participantFunctionCode = value.code;
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />

                                  <CodesetSelect
                                    label="Endorsement Function"
                                    codesetCode="B06"
                                    filter="#Endorsement"
                                    value={
                                      contractMarket.endorsementParticipantFunctionCode
                                        ? {
                                            code: contractMarket.endorsementParticipantFunctionCode,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated[
                                        marketIdx
                                      ].endorsementParticipantFunctionCode =
                                        value.code;
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />
                                  <CodesetSelect
                                    label="Claim Function"
                                    codesetCode="B06"
                                    filter="#Claim"
                                    value={
                                      contractMarket.claimParticipantFunctionCode
                                        ? {
                                            code: contractMarket.claimParticipantFunctionCode,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated[
                                        marketIdx
                                      ].claimParticipantFunctionCode =
                                        value.code;
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />
                                  <CodesetSelect
                                    label="Declaration Function"
                                    codesetCode="B06"
                                    filter="#Declaration"
                                    value={
                                      contractMarket.declarationParticipantFunctionCode
                                        ? {
                                            code: contractMarket.declarationParticipantFunctionCode,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated[
                                        marketIdx
                                      ].declarationParticipantFunctionCode =
                                        value.code;
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />
                                </SimpleGrid>

                                <SimpleGrid columns={{ base: 1, md: 7 }} mb={2}>
                                  <CodesetSelect
                                    label="Line To Stand"
                                    codesetCode="JVRI018"
                                    value={
                                      contractMarket.lineToStandIndicator
                                        ? {
                                            code: contractMarket.lineToStandIndicator,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated[marketIdx].lineToStandIndicator =
                                        value.code;
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />

                                  <CodesetSelect
                                    label="Participation Basis"
                                    codesetCode="B52"
                                    value={
                                      contractMarket.participationBasis
                                        ? {
                                            code: contractMarket.participationBasis,
                                            description: "",
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated[marketIdx].participationBasis =
                                        value.code;
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />
                                  <FormControl>
                                    <FormLabel textAlign="right" width={150}>
                                      Minimum
                                    </FormLabel>
                                    <Input
                                      textAlign="right"
                                      placeholder="0.00"
                                      width={150}
                                      type="number"
                                      value={contractMarket.minimumShare || 0}
                                      onChange={(e) => {
                                        const updated = [
                                          ...section.contractMarkets,
                                        ];
                                        updated[marketIdx].minimumShare =
                                          parseFloat(e.target.value) || 0;
                                        updateSection(
                                          idx,
                                          "contractMarkets",
                                          updated
                                        );
                                      }}
                                    />
                                  </FormControl>
                                  <FormControl>
                                    <FormLabel textAlign="right" width={150}>
                                      Maximum
                                    </FormLabel>
                                    <Input
                                      textAlign="right"
                                      placeholder="0.00"
                                      width={150}
                                      type="number"
                                      value={contractMarket.maximumShare || 0}
                                      onChange={(e) => {
                                        const updated = [
                                          ...section.contractMarkets,
                                        ];
                                        updated[marketIdx].maximumShare =
                                          parseFloat(e.target.value) || 0;
                                        updateSection(
                                          idx,
                                          "contractMarkets",
                                          updated
                                        );
                                      }}
                                    />
                                  </FormControl>
                                  <FormControl>
                                    <FormLabel textAlign="right" width={150}>
                                      Quoted
                                    </FormLabel>
                                    <Input
                                      textAlign="right"
                                      placeholder="0.00"
                                      width={150}
                                      type="number"
                                      value={contractMarket.quotedShare || 0}
                                      onChange={(e) => {
                                        const updated = [
                                          ...section.contractMarkets,
                                        ];
                                        updated[marketIdx].quotedShare =
                                          parseFloat(e.target.value) || 0;
                                        updateSection(
                                          idx,
                                          "contractMarkets",
                                          updated
                                        );
                                      }}
                                    />
                                  </FormControl>
                                  <FormControl>
                                    <FormLabel textAlign="right" width={150}>
                                      Written
                                    </FormLabel>
                                    <Input
                                      textAlign="right"
                                      placeholder="0.00"
                                      width={150}
                                      type="number"
                                      value={contractMarket.writtenShare || 0}
                                      onChange={(e) => {
                                        const updated = [
                                          ...section.contractMarkets,
                                        ];
                                        updated[marketIdx].writtenShare =
                                          parseFloat(e.target.value) || 0;
                                        updateSection(
                                          idx,
                                          "contractMarkets",
                                          updated
                                        );
                                      }}
                                    />
                                  </FormControl>
                                  <FormControl>
                                    <FormLabel textAlign="right" width={150}>
                                      Signed
                                    </FormLabel>
                                    <Input
                                      textAlign="right"
                                      placeholder="0.00"
                                      width={150}
                                      type="number"
                                      value={contractMarket.signedShare || 0}
                                      onChange={(e) => {
                                        const updated = [
                                          ...section.contractMarkets,
                                        ];
                                        updated[marketIdx].signedShare =
                                          parseFloat(e.target.value) || 0;
                                        updateSection(
                                          idx,
                                          "contractMarkets",
                                          updated
                                        );
                                      }}
                                    />
                                  </FormControl>
                                </SimpleGrid>

                                <Box>
                                  <ReferencesEditor
                                    references={
                                      section.contractMarkets[marketIdx]
                                        .references || []
                                    }
                                    onChange={(references) => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated[marketIdx].references =
                                        references;
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />
                                  <ClassificationsEditor
                                    classifications={
                                      section.contractMarkets[marketIdx]
                                        .classifications || []
                                    }
                                    onChange={(classifications) => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated[marketIdx].classifications =
                                        classifications;
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />
                                </Box>

                                <HStack justify="flex-end">
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlineTrash />}
                                    aria-label="Delete Carrier"
                                    onClick={() => {
                                      const updated = [
                                        ...section.contractMarkets,
                                      ];
                                      updated.splice(marketIdx, 1);
                                      updateSection(
                                        idx,
                                        "contractMarkets",
                                        updated
                                      );
                                    }}
                                  />
                                </HStack>
                              </Box>
                            )
                          )}
                        </TabPanel>

                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Clauses</Heading>

                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              onClick={() => {}}
                            />
                          </HStack>
                        </TabPanel>
                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Schedules</Heading>

                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              onClick={() => {
                                const newSchedules = [
                                  ...(section.schedules || []),
                                  {
                                    id: crypto.randomUUID(),
                                    type: "",
                                    description: "",
                                    scheduleEntries: [],
                                  },
                                ];
                                updateSection(idx, "schedules", newSchedules);
                              }}
                            />
                          </HStack>

                          {(section.schedules || []).map(
                            (schedule, schedIdx) => (
                              <Box
                                key={schedule.id}
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                mb={4}
                                borderColor="gray.500">
                                <UuidPreview uuid={schedule.id} />
                                <CodesetSelect
                                  label="Schedule Type"
                                  codesetCode="B57"
                                  value={
                                    schedule.type
                                      ? {
                                          code: schedule.type,
                                          description: "",
                                        }
                                      : null
                                  }
                                  onChange={(value) => {
                                    const updated = [...section.schedules];
                                    updated[schedIdx].type = value.code;
                                    updateSection(idx, "schedules", updated);
                                  }}
                                />
                                <FormControl mb={2}>
                                  <FormLabel>Description</FormLabel>
                                  <Input
                                    placeholder="Schedule Description"
                                    value={schedule.description || ""}
                                    onChange={(e) => {
                                      const updated = [...section.schedules];
                                      updated[schedIdx].description =
                                        e.target.value;
                                      updateSection(idx, "schedules", updated);
                                    }}
                                  />
                                </FormControl>

                                <HStack alignItems="center">
                                  <Heading size="md">Entries</Heading>

                                  <IconButton
                                    icon={<HiOutlinePlus />}
                                    size="md"
                                    onClick={() => {
                                      const newEntry = {
                                        id: crypto.randomUUID(),
                                        description: "",
                                        value: "",
                                        references: [],
                                      };
                                      const updated = [...section.schedules];
                                      updated[schedIdx].scheduleEntries.push(
                                        newEntry
                                      );
                                      updateSection(idx, "schedules", updated);
                                    }}
                                  />
                                </HStack>

                                <Box mt={2}>
                                  {(schedule.scheduleEntries || []).map(
                                    (entry, entryIdx) => (
                                      <Box
                                        key={entry.id}
                                        borderWidth="1px"
                                        borderRadius="md"
                                        p={4}
                                        mb={2}
                                        borderColor="gray.500">
                                        <UuidPreview uuid={entry.id} />
                                        <FormControl mb={2}>
                                          <FormLabel>Description</FormLabel>
                                          <Input
                                            placeholder="Entry Description"
                                            value={entry.description || ""}
                                            onChange={(e) => {
                                              const updated = [
                                                ...section.schedules,
                                              ];
                                              updated[schedIdx].scheduleEntries[
                                                entryIdx
                                              ].description = e.target.value;
                                              updateSection(
                                                idx,
                                                "schedules",
                                                updated
                                              );
                                            }}
                                          />
                                        </FormControl>
                                        <Tabs
                                          orientation="horizontal"
                                          variant="line"
                                          colorScheme="linkedin">
                                          <TabList>
                                            <Tab>References</Tab>
                                            <Tab>Classifications</Tab>
                                            <Tab>Amounts</Tab>
                                            <Tab>DateTimes</Tab>
                                          </TabList>
                                          <TabPanels>
                                            <TabPanel>
                                              <ReferencesEditor
                                                filter="#ContractSchedule"
                                                references={
                                                  entry.references || []
                                                }
                                                onChange={(references) => {
                                                  const updated = [
                                                    ...section.schedules,
                                                  ];
                                                  updated[
                                                    schedIdx
                                                  ].scheduleEntries[
                                                    entryIdx
                                                  ].references = references;
                                                  updateSection(
                                                    idx,
                                                    "schedules",
                                                    updated
                                                  );
                                                }}
                                              />
                                            </TabPanel>
                                            <TabPanel>
                                              <ClassificationsEditor
                                                filter="#ContractSchedule"
                                                classifications={
                                                  entry.classifications || []
                                                }
                                                onChange={(classifications) => {
                                                  const updated = [
                                                    ...section.schedules,
                                                  ];
                                                  updated[
                                                    schedIdx
                                                  ].scheduleEntries[
                                                    entryIdx
                                                  ].classifications =
                                                    classifications;
                                                  updateSection(
                                                    idx,
                                                    "schedules",
                                                    updated
                                                  );
                                                }}
                                              />
                                            </TabPanel>
                                            <TabPanel>
                                              <AmountsEditor
                                                filter="#ContractSchedule"
                                                amounts={entry.amounts || []}
                                                onChange={(amounts) => {
                                                  const updated = [
                                                    ...section.schedules,
                                                  ];
                                                  updated[
                                                    schedIdx
                                                  ].scheduleEntries[
                                                    entryIdx
                                                  ].amounts = amounts;
                                                  updateSection(
                                                    idx,
                                                    "schedules",
                                                    updated
                                                  );
                                                }}
                                              />
                                            </TabPanel>
                                            <TabPanel>
                                              <DateTimesEditor
                                                filter="#ContractSchedule"
                                                dateTimes={
                                                  entry.dateTimes || []
                                                }
                                                onChange={(dateTimes) => {
                                                  const updated = [
                                                    ...section.schedules,
                                                  ];
                                                  updated[
                                                    schedIdx
                                                  ].scheduleEntries[
                                                    entryIdx
                                                  ].dateTimes = dateTimes;
                                                  updateSection(
                                                    idx,
                                                    "schedules",
                                                    updated
                                                  );
                                                }}
                                              />
                                            </TabPanel>
                                          </TabPanels>
                                        </Tabs>

                                        <HStack justify="flex-end">
                                          <IconButton
                                            size="md"
                                            icon={<HiOutlineTrash />}
                                            aria-label="Delete Entry"
                                            onClick={() => {
                                              const updated = [
                                                ...section.schedules,
                                              ];
                                              updated[
                                                schedIdx
                                              ].scheduleEntries.splice(
                                                entryIdx,
                                                1
                                              );
                                              updateSection(
                                                idx,
                                                "schedules",
                                                updated
                                              );
                                            }}
                                          />
                                        </HStack>
                                      </Box>
                                    )
                                  )}
                                </Box>

                                <HStack justify="flex-end">
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlineTrash />}
                                    aria-label="Delete Schedule"
                                    onClick={() => {
                                      const updated = [...section.schedules];
                                      updated.splice(schedIdx, 1);
                                      updateSection(idx, "schedules", updated);
                                    }}
                                  />
                                </HStack>
                              </Box>
                            )
                          )}
                        </TabPanel>
                        <TabPanel>
                          <HStack alignItems="center">
                            <Heading size="md">Regulatory Provisions</Heading>

                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="md"
                              onClick={() => {
                                const newRegs = [
                                  ...(section.regulatoryProvisions || []),
                                  {
                                    id: crypto.randomUUID(),
                                    regulatoryRiskLocations: [],
                                    classifications: [],
                                    amounts: [],
                                    dateTimes: [],
                                    allocations: [],
                                  },
                                ];
                                updateSection(
                                  idx,
                                  "regulatoryProvisions",
                                  newRegs
                                );
                              }}
                            />
                          </HStack>

                          {(section.regulatoryProvisions || []).map(
                            (prov, provIdx) => (
                              <Box
                                key={prov.id}
                                borderWidth="1px"
                                borderRadius="md"
                                p={4}
                                mb={6}
                                borderColor="gray.500">
                                <UuidPreview uuid={prov.id} />

                                {/* Risk Locations */}
                                <HStack alignItems="center">
                                  <Heading size="md">
                                    Regulatory Risk Locations
                                  </Heading>
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlinePlus />}
                                    aria-label="Add Regulatory Risk Location"
                                    onClick={() => {}}
                                  />
                                </HStack>
                                <LocationsEditor
                                  filter="#RegulatoryProvision"
                                  locations={prov.regulatoryRiskLocations || []}
                                  onChange={(locations) => {
                                    const updated = [
                                      ...section.regulatoryProvisions,
                                    ];
                                    updated[provIdx].regulatoryRiskLocations =
                                      locations;
                                    updateSection(
                                      idx,
                                      "regulatoryProvisions",
                                      updated
                                    );
                                  }}
                                />

                                {/* Classifications */}
                                <HStack alignItems="center">
                                  <Heading size="md">Classifications</Heading>
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlinePlus />}
                                    aria-label="Add Classification"
                                    onClick={() => {}}
                                  />
                                </HStack>
                                <ClassificationsEditor
                                  filter="#RegulatoryProvision"
                                  classifications={prov.classifications || []}
                                  onChange={(classifications) => {
                                    const updated = [
                                      ...section.regulatoryProvisions,
                                    ];
                                    updated[provIdx].classifications =
                                      classifications;
                                    updateSection(
                                      idx,
                                      "regulatoryProvisions",
                                      updated
                                    );
                                  }}
                                />

                                {/* Amounts */}
                                <HStack alignItems="center">
                                  <Heading size="md">Amounts</Heading>
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlinePlus />}
                                    aria-label="Add Amount"
                                    onClick={() => {}}
                                  />
                                </HStack>
                                <AmountsEditor
                                  filter="#RegulatoryProvision"
                                  amounts={prov.amounts || []}
                                  onChange={(amounts) => {
                                    const updated = [
                                      ...section.regulatoryProvisions,
                                    ];
                                    updated[provIdx].amounts = amounts;
                                    updateSection(
                                      idx,
                                      "regulatoryProvisions",
                                      updated
                                    );
                                  }}
                                />

                                {/* DateTimes */}
                                <HStack alignItems="center">
                                  <Heading size="md">DateTimes</Heading>
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlinePlus />}
                                    aria-label="Add DateTime"
                                    onClick={() => {}}
                                  />
                                </HStack>
                                <DateTimesEditor
                                  dateTimes={prov.dateTimes || []}
                                  onChange={(dateTimes) => {
                                    const updated = [
                                      ...section.regulatoryProvisions,
                                    ];
                                    updated[provIdx].dateTimes = dateTimes;
                                    updateSection(
                                      idx,
                                      "regulatoryProvisions",
                                      updated
                                    );
                                  }}
                                />

                                {/* Allocations */}
                                <HStack alignItems="center">
                                  <Heading size="md">Allocations</Heading>
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlinePlus />}
                                    aria-label="Add Allocation"
                                    onClick={() => {}}
                                  />
                                </HStack>
                                <HStack justify="flex-end">
                                  <IconButton
                                    size="md"
                                    icon={<HiOutlineTrash />}
                                    aria-label="Delete Regulatory Provision"
                                    onClick={() => {
                                      const updated = [
                                        ...section.regulatoryProvisions,
                                      ];
                                      updated.splice(provIdx, 1);
                                      updateSection(
                                        idx,
                                        "regulatoryProvisions",
                                        updated
                                      );
                                    }}
                                  />
                                </HStack>
                              </Box>
                            )
                          )}
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default GRLCPlacementEditor;
